import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import { PokemonV2, toDefence, toOffence, rankCorrectionEnum, individualValueEnum } from '../pokemon/pokemon';
import { EXCLUDED_POKEMON_IDS } from '../pokemon/excluded-pokemon-ids';
import { usePokemonList } from '../pokemon/usePokemonList';
import type { PokemonOption, calcOtherSetting } from '../types/pokemon';
import { PokemonType } from '../types/type';
import { calc_interface } from '../calc-damage/calc_damage';
import { PersonalityRate } from '../types/damage';
import type { retCalc } from '../types/damage';
import { otherSetting, WEATHER_EMPTY, BATTLE_FIELD_EMPTY } from '../OtherSetting';
import { getAllAttackMoves, getMoveById, getMovesByIds } from '../lib/move-access';
import { POKEMON_ITEMS } from '../lib/item';
import { StatInputs } from '../types/stat';
import { storageClient } from '../storage';
import ability_list from '../ability/ability-list';
import { Ability } from '../types/ability';
import type { AttackPokemonData } from '../types/attack';
import {
  POKEMON_ITEM_ID_NO_SET as OFFENCE_ITEM_ID_NO_SET,
  OFFENCE_ITEM_ID_KODAWARIHATIMAKI,
  OFFENCE_ITEM_ID_KODAWARIMEGANE,
  OFFENCE_ITEM_ID_INOTINOTAMA,
  OFFENCE_ITEM_ID_TIKARANOHACHIMAKI,
  OFFENCE_ITEM_ID_MONOSHIRIMEGANE,
  OFFENCE_ITEM_ID_PUNCHGLOVE,
  OFFENCE_ITEM_ID_NORMAL_JEWEL,
  OFFENCE_ITEM_ID_KODAWARISUKAFU,
  OFFENCE_ITEM_ID_RUSTED_SWORD,
  OFFENCE_ITEM_ID_RUSTED_SHIELD,
  OFFENCE_ITEM_ID_FLAME_PLATE,
  OFFENCE_ITEM_ID_SPLASH_PLATE,
  OFFENCE_ITEM_ID_ZAP_PLATE,
  OFFENCE_ITEM_ID_MEADOW_PLATE,
  OFFENCE_ITEM_ID_ICICLE_PLATE,
  OFFENCE_ITEM_ID_FIST_PLATE,
  OFFENCE_ITEM_ID_TOXIC_PLATE,
  OFFENCE_ITEM_ID_EARTH_PLATE,
  OFFENCE_ITEM_ID_SKY_PLATE,
  OFFENCE_ITEM_ID_MIND_PLATE,
  OFFENCE_ITEM_ID_INSECT_PLATE,
  OFFENCE_ITEM_ID_STONE_PLATE,
  OFFENCE_ITEM_ID_SPOOKY_PLATE,
  OFFENCE_ITEM_ID_DRACO_PLATE,
  OFFENCE_ITEM_ID_DREAD_PLATE,
  OFFENCE_ITEM_ID_IRON_PLATE,
  OFFENCE_ITEM_ID_PIXIE_PLATE,
  OFFENCE_ITEM_ID_AQUA_CASSETTE,
  OFFENCE_ITEM_ID_INAZUMA_CASSETTE,
  OFFENCE_ITEM_ID_BLAZE_CASSETTE,
  OFFENCE_ITEM_ID_FREEZE_CASSETTE,
  OFFENCE_ITEM_ID_SHIRATAMA,
  OFFENCE_ITEM_ID_KONGOUDAMA,
  OFFENCE_ITEM_ID_HAKKINDAMA,
  OFFENCE_ITEM_ID_BENIIRO_NO_TAMA,
  OFFENCE_ITEM_ID_AIIRO_NO_TAMA,
  OFFENCE_ITEM_ID_KOKORONOSHIZUKU,
  OFFENCE_ITEM_ID_TATSUJIN_OBI,
  OFFENCE_ITEM_ID_DENKIDAMA,
  OFFENCE_ITEM_ID_FUTOIHONE,
  OFFENCE_ITEM_ID_DAIKONGOUDAMA,
  OFFENCE_ITEM_ID_DAISHIRATAMA,
  OFFENCE_ITEM_ID_DAIHAKKINDAMA,
  OFFENCE_ITEM_ID_IDONOMEN,
  OFFENCE_ITEM_ID_KAMADONOMEN,
  OFFENCE_ITEM_ID_ISHIZUENOMEN,
  POKEMON_ITEM_ID_NO_SET as DEFENCE_ITEM_ID_NO_SET,
  DEFENCE_ITEM_ID_ASSAULT_VEST,
  DEFENCE_ITEM_ID_SINKANOKISEKI,
  DEFENCE_ITEM_ID_HANGENKINOMI,
  DEFENCE_ITEM_ID_BALLOON,
  DEFENCE_ITEM_ID_RUSTED_SWORD,
  DEFENCE_ITEM_ID_RUSTED_SHIELD,
  DEFENCE_ITEM_ID_DAIKONGOUDAMA,
  DEFENCE_ITEM_ID_DAISHIRATAMA,
  DEFENCE_ITEM_ID_DAIHAKKINDAMA,
  DEFENCE_ITEM_ID_BENIIRO_NO_TAMA,
  DEFENCE_ITEM_ID_AIIRO_NO_TAMA,
  DEFENCE_ITEM_ID_IDONOMEN,
  DEFENCE_ITEM_ID_KAMADONOMEN,
  DEFENCE_ITEM_ID_ISHIZUENOMEN,
  POKEMON_ITEM_ID as OFFENCE_ITEM_ID,
  POKEMON_ITEM_ID as DEFENCE_ITEM_ID
} from '../types/item';
import { Move, isBodyPressMove, isDamagingMove, isHakidasuMove, isHundonokobushiMove, isIkasamaMove, isKishikaiseiMove, isOhakamairiMove, isPhysicalMove, isRenzokuMove, isSpecialMove, isStoredPowerMove, isWringOutMove } from '../move/move';
import { MAX_STAT_EFFORT, MAX_TOTAL_EFFORT } from '../constants/effort';
import { hasPokemonStatus, isJuudenStatus, setPokemonStatus } from '../lib/pokemon/status';
import type { MoveDamageSetting } from '../types/damage-setting';
// 未使用の stat_calc インポートをコメントアウト
/*
import {
  calcRealValueHPStat,
  calcRealValueAttackDefencePokemon,
  calcRealValueSpecialAttackDefencePokemon,
  calcRealValueDeffenceDefencePokemon,
  calcRealValueSpecialDeffenceDefencePokemon,
  calcRealValueSpeedDefencePokemon
} from '../lib/stat/real-value';
*/
import { debounce } from '../utils/debounce';
import LeftSidebar from '../LeftSideBar';
import { useLeftSideBarViewModel } from '../LeftSideBar/useViewModel';
import type { ItemOption } from '../types/item';
import MainDisplay from '../MainDisplay';
import { getPlateItemIdForArceusPokemon } from '../pokemon/arceus';
import { getMegaStoneItemIdForPokemon, isMegaStoneItemId } from '../lib/mega-stone';
import { canPokemonHoldItem, getFixedItemIdForPokemonEntity } from '../lib/pokemon-config';
import { formatPokemonDisplayName } from '../utils/pokemon-display-name';
import {
  DEFAULT_COMPREHENSIVE_UI_FLAGS,
  createOpponentSpeedSettings,
  type ComprehensiveUiFlags,
  type OpponentCard,
  type OpponentSpeedSettings,
} from '../types/comprehensive';

const ITEM_ORDER = [
  DEFENCE_ITEM_ID_NO_SET,
  DEFENCE_ITEM_ID_ASSAULT_VEST,
  DEFENCE_ITEM_ID_SINKANOKISEKI,
  DEFENCE_ITEM_ID_HANGENKINOMI,
  DEFENCE_ITEM_ID_BALLOON,
  OFFENCE_ITEM_ID_KODAWARIHATIMAKI,
  OFFENCE_ITEM_ID_KODAWARIMEGANE,
  OFFENCE_ITEM_ID_KODAWARISUKAFU,
  OFFENCE_ITEM_ID_INOTINOTAMA,
  OFFENCE_ITEM_ID_TIKARANOHACHIMAKI,
  OFFENCE_ITEM_ID_MONOSHIRIMEGANE,
  OFFENCE_ITEM_ID_PUNCHGLOVE,
  OFFENCE_ITEM_ID_NORMAL_JEWEL,
  OFFENCE_ITEM_ID_TATSUJIN_OBI,
  OFFENCE_ITEM_ID_RUSTED_SWORD,
  OFFENCE_ITEM_ID_RUSTED_SHIELD,
  OFFENCE_ITEM_ID_FLAME_PLATE,
  OFFENCE_ITEM_ID_SPLASH_PLATE,
  OFFENCE_ITEM_ID_ZAP_PLATE,
  OFFENCE_ITEM_ID_MEADOW_PLATE,
  OFFENCE_ITEM_ID_ICICLE_PLATE,
  OFFENCE_ITEM_ID_FIST_PLATE,
  OFFENCE_ITEM_ID_TOXIC_PLATE,
  OFFENCE_ITEM_ID_EARTH_PLATE,
  OFFENCE_ITEM_ID_SKY_PLATE,
  OFFENCE_ITEM_ID_MIND_PLATE,
  OFFENCE_ITEM_ID_INSECT_PLATE,
  OFFENCE_ITEM_ID_STONE_PLATE,
  OFFENCE_ITEM_ID_SPOOKY_PLATE,
  OFFENCE_ITEM_ID_DRACO_PLATE,
  OFFENCE_ITEM_ID_DREAD_PLATE,
  OFFENCE_ITEM_ID_IRON_PLATE,
  OFFENCE_ITEM_ID_PIXIE_PLATE,
  OFFENCE_ITEM_ID_AQUA_CASSETTE,
  OFFENCE_ITEM_ID_INAZUMA_CASSETTE,
  OFFENCE_ITEM_ID_BLAZE_CASSETTE,
  OFFENCE_ITEM_ID_FREEZE_CASSETTE,
  OFFENCE_ITEM_ID_SHIRATAMA,
  OFFENCE_ITEM_ID_KONGOUDAMA,
  OFFENCE_ITEM_ID_HAKKINDAMA,
  OFFENCE_ITEM_ID_KOKORONOSHIZUKU,
  OFFENCE_ITEM_ID_DAIKONGOUDAMA,
  OFFENCE_ITEM_ID_DAISHIRATAMA,
  OFFENCE_ITEM_ID_DAIHAKKINDAMA,
  OFFENCE_ITEM_ID_IDONOMEN,
  OFFENCE_ITEM_ID_KAMADONOMEN,
  OFFENCE_ITEM_ID_ISHIZUENOMEN,
] as const;

// 相手追加時は共通条件を未選択で開始する
const createEmptyOtherSetting = (): otherSetting => ({
  weather: WEATHER_EMPTY,
  battle_field: BATTLE_FIELD_EMPTY,
  battleStatuses: [],
});

// 「しんかのきせき」を自動で設定したいポケモン（固定はしない）
// NOTE: 選択直後の初期値として付与するだけで、ユーザーが他の道具に変更できる。
const EVOLITE_AUTO_SET_POKEMON_IDS = new Set<number>([
  233, // ポリゴン2
  113, // ラッキー
  772, // タイプ：ヌル
  1011, // カミッチュ
  123, // ストライク
  10234, // ハリーセン(ヒスイのすがた)
  35, // ピッピ
  161, // オタチ
  198, // ヤミカラス
  559, // ズルッグ
  447, // リオル
  125, // エレブー
  126, // ブーバー
  737, // デンヂムシ
  82, // レアコイル
]);

const shouldAutoSetEviolite = (pokemonId: number): boolean =>
  EVOLITE_AUTO_SET_POKEMON_IDS.has(pokemonId);

// 「でんきだま」を自動で設定したいポケモン（固定はしない）
// NOTE: フォーム違い（アイドル等）も species_id=25（ピカチュウ）なので species_id で判定する。
const shouldAutoSetLightBall = (pokemon: PokemonV2): boolean =>
  pokemon.species_id === 25;

// 「ふといホネ」を自動で設定したいポケモン（固定はしない）
// NOTE: ガラガラ(アローラのすがた) も species_id=105（ガラガラ）なので species_id で判定する。
const shouldAutoSetThickClub = (pokemon: PokemonV2): boolean =>
  pokemon.species_id === 104 || pokemon.species_id === 105;

const {
  attackList: attackListStorage,
  defenceList: defenceListStorage,
  comprehensiveList: comprehensiveListStorage,
} = storageClient.pokemon;





export interface AttackStatInputs {
  attack_effort: number;
  special_attack_effort: number;
  defense_effort: number;
  special_defense_effort: number;
  hp_effort: number;
  speed_effort: number;
  attack_personality: PersonalityRate;
  special_attack_personality: PersonalityRate;
  defense_personality: PersonalityRate;
  special_defense_personality: PersonalityRate;
  speed_personality: PersonalityRate;
  attack_iv?: number;
  defense_iv?: number;
  special_attack_iv?: number;
  special_defense_iv?: number;
  speed_iv?: number;
}


// AttackPokemonData は types/attack.ts に集約

export const initialStatInputs: StatInputs = {
  hp_effort: 0,
  attack_effort: 0,
  defense_effort: 0,
  special_attack_effort: 0,
  special_defense_effort: 0,
  speed_effort: 0,
  attack_personality: 1.0,
  defense_personality: 1.0,
  special_attack_personality: 1.0,
  special_defense_personality: 1.0,
  speed_personality: 1.0,
};

export const initialAttackStatInputs: AttackStatInputs = {
  attack_effort: 32,
  special_attack_effort: 32,
  defense_effort: 0,
  special_defense_effort: 0,
  hp_effort: 0,
  speed_effort: 0,
  attack_personality: 1.0,
  special_attack_personality: 1.0,
  defense_personality: 1.0,
  special_defense_personality: 1.0,
  speed_personality: 1.0,
  attack_iv: 31,
  defense_iv: 31,
  special_attack_iv: 31,
  special_defense_iv: 31,
  speed_iv: 31,
};

// 総合モード用：防御側プロファイルの初期値を生成する
const createComprehensiveDefenseProfile = (foundPokemon: PokemonV2): AttackPokemonData => {
  const defenceVersion = toDefence(JSON.parse(JSON.stringify(foundPokemon)));

  // 使用率が高い特性を初期選択
  if (defenceVersion.ability_list && defenceVersion.ability_list.length > 0) {
    if (defenceVersion.often_used_tokusei && defenceVersion.often_used_tokusei.length > 0) {
      const mostUsedAbilityId = defenceVersion.often_used_tokusei[0];
      const mostUsedAbility = defenceVersion.ability_list.find((ability) => ability.id === mostUsedAbilityId);
      if (mostUsedAbility) {
        defenceVersion.selected_ability_id = mostUsedAbility.id;
        defenceVersion.selected_ability = mostUsedAbility;
      }
    } else {
      defenceVersion.selected_ability_id = defenceVersion.ability_list[0].id;
      defenceVersion.selected_ability = defenceVersion.ability_list[0];
    }
  }

  // 防御側の初期アイテム（既存の防御モードと同じルール）
  let itemToSet = DEFENCE_ITEM_ID_NO_SET;
  const megaStoneItemId = getMegaStoneItemIdForPokemon(foundPokemon);
  if (megaStoneItemId != null) {
    itemToSet = megaStoneItemId;
  } else if (foundPokemon.id === 10245) {
    itemToSet = DEFENCE_ITEM_ID_DAIKONGOUDAMA;
  } else if (foundPokemon.id === 10246) {
    itemToSet = DEFENCE_ITEM_ID_DAISHIRATAMA;
  } else if (foundPokemon.id === 10007) {
    itemToSet = DEFENCE_ITEM_ID_DAIHAKKINDAMA;
  } else if (foundPokemon.id === 10077) {
    itemToSet = DEFENCE_ITEM_ID_AIIRO_NO_TAMA;
  } else if (foundPokemon.id === 10078) {
    itemToSet = DEFENCE_ITEM_ID_BENIIRO_NO_TAMA;
  } else if (foundPokemon.id === 10188) {
    itemToSet = DEFENCE_ITEM_ID_RUSTED_SWORD;
  } else if (foundPokemon.id === 10189) {
    itemToSet = DEFENCE_ITEM_ID_RUSTED_SHIELD;
	  } else if (foundPokemon.id === 10273) {
	    itemToSet = DEFENCE_ITEM_ID_IDONOMEN;
	  } else if (foundPokemon.id === 10274) {
	    itemToSet = DEFENCE_ITEM_ID_KAMADONOMEN;
	  } else if (foundPokemon.id === 10275) {
	    itemToSet = DEFENCE_ITEM_ID_ISHIZUENOMEN;
	  } else if (shouldAutoSetThickClub(foundPokemon)) {
	    itemToSet = OFFENCE_ITEM_ID_FUTOIHONE;
	  } else if (shouldAutoSetLightBall(foundPokemon)) {
	    itemToSet = OFFENCE_ITEM_ID_DENKIDAMA;
	  } else if (shouldAutoSetEviolite(foundPokemon.id)) {
	    itemToSet = DEFENCE_ITEM_ID_SINKANOKISEKI;
	  } else {
	    const arceusDefensePlate = getPlateItemIdForArceusPokemon(foundPokemon);
	    if (arceusDefensePlate) {
      itemToSet = arceusDefensePlate as DEFENCE_ITEM_ID;
    }
  }

  defenceVersion.selected_item_id = itemToSet;

  defenceVersion.effective_slider_step = {
    hp: 0,
    attack: 0,
    defense: 0,
    special_attack: 0,
    special_defense: 0,
    speed: 0,
  };

  defenceVersion.personality = {
    attack: 1.0,
    defense: 1.0,
    special_attack: 1.0,
    special_defense: 1.0,
    speed: 1.0,
  };

  return {
    pokemon: defenceVersion,
    // 相手カードに追加するポケモンは、原則A/Cに32ずつ努力値を振った状態で初期化する
    statInputs: { ...initialAttackStatInputs },
    id: `comprehensive-defense-${Date.now()}-${Math.random()}`,
    selectedAbilityId: defenceVersion.selected_ability_id,
    selectedAbility: defenceVersion.selected_ability,
    selectedItemId: itemToSet,
    rankCorrection: 0,
    isBurned: false,
    isPoisoned: false,
    isParalyzed: false,
    terasuType: undefined,
  };
};

// 総合モード用：攻撃側プロファイルの初期値を生成する
const createComprehensiveOffenseProfile = (foundPokemon: PokemonV2): AttackPokemonData => {
  const offenceVersion = toDefence(JSON.parse(JSON.stringify(foundPokemon)));

  let selectedAbilityId: number | undefined;
  let selectedAbility: Ability | undefined;
  if (offenceVersion.ability_list && offenceVersion.ability_list.length > 0) {
    selectedAbilityId = offenceVersion.ability_list[0].id;
    selectedAbility = offenceVersion.ability_list[0];
  }

  let initialRankCorrection: rankCorrectionEnum = 0;
  let selectedItemId: OFFENCE_ITEM_ID | undefined;
  const megaStoneItemId = getMegaStoneItemIdForPokemon(foundPokemon);
  if (megaStoneItemId != null) {
    selectedItemId = megaStoneItemId;
  } else if (foundPokemon.id === 10187 || foundPokemon.id === 10188) {
    initialRankCorrection = 1;
    if (foundPokemon.id === 10188) {
      selectedItemId = OFFENCE_ITEM_ID_RUSTED_SWORD;
    }
  } else if (foundPokemon.id === 10189) {
    selectedItemId = OFFENCE_ITEM_ID_RUSTED_SHIELD;
  } else if (foundPokemon.id === 10245) {
    selectedItemId = OFFENCE_ITEM_ID_DAIKONGOUDAMA;
  } else if (foundPokemon.id === 10246) {
    selectedItemId = OFFENCE_ITEM_ID_DAISHIRATAMA;
  } else if (foundPokemon.id === 10007) {
    selectedItemId = OFFENCE_ITEM_ID_DAIHAKKINDAMA;
  } else if (foundPokemon.id === 10077) {
    selectedItemId = OFFENCE_ITEM_ID_AIIRO_NO_TAMA;
	  } else if (foundPokemon.id === 10078) {
	    selectedItemId = OFFENCE_ITEM_ID_BENIIRO_NO_TAMA;
	  } else if (foundPokemon.id === 10273) {
	    selectedItemId = OFFENCE_ITEM_ID_IDONOMEN;
	  } else if (foundPokemon.id === 10274) {
	    selectedItemId = OFFENCE_ITEM_ID_KAMADONOMEN;
	  } else if (foundPokemon.id === 10275) {
	    selectedItemId = OFFENCE_ITEM_ID_ISHIZUENOMEN;
	  } else if (shouldAutoSetThickClub(foundPokemon)) {
	    selectedItemId = OFFENCE_ITEM_ID_FUTOIHONE;
	  } else if (shouldAutoSetLightBall(foundPokemon)) {
	    selectedItemId = OFFENCE_ITEM_ID_DENKIDAMA;
	  }

  const arceusPlateForAttack = getPlateItemIdForArceusPokemon(foundPokemon);
  if (megaStoneItemId == null && arceusPlateForAttack) {
    selectedItemId = arceusPlateForAttack as OFFENCE_ITEM_ID;
  }

  let terasuType: number | undefined;
  if (foundPokemon.id === 10277) {
    terasuType = 19;
  }

  if (selectedItemId) {
    offenceVersion.selected_item_id = selectedItemId;
  }

  return {
    pokemon: offenceVersion,
    // 相手カードに追加するポケモンは、原則A/Cに32ずつ努力値を振った状態で初期化する
    statInputs: { ...initialAttackStatInputs },
    id: `comprehensive-offense-${Date.now()}-${Math.random()}`,
    selectedAbilityId,
    selectedAbility,
    selectedItemId,
    rankCorrection: initialRankCorrection,
    isBurned: false,
    isPoisoned: false,
    isParalyzed: false,
    terasuType,
  };
};

// 相手カード追加時の「相手から受けるダメージ」用に、使用率上位の攻撃技（物理/特殊）を4つまで初期選択する。
// - 変化技は除外する
// - 使用率圏外（often_used_move に無い）技で埋めない
const getDefaultOpponentAssumedMoveIds = (pokemon: PokemonV2): number[] => {
  const moveIds = pokemon.often_used_move ?? [];
  if (moveIds.length === 0) {
    return [];
  }

  const selected: number[] = [];
  const seen = new Set<number>();
  for (const moveId of moveIds) {
    if (selected.length >= 4) {
      break;
    }
    if (moveId <= 0 || seen.has(moveId)) {
      continue;
    }
    if (!pokemon.move_id_list?.includes(moveId)) {
      continue;
    }
    const move = getMoveById(moveId);
    if (!move || !isDamagingMove(move)) {
      continue;
    }
    selected.push(moveId);
    seen.add(moveId);
  }
  return selected;
};

// 総合モード用：AttackPokemonData を深いコピーで複製する
const cloneAttackPokemonData = (profile: AttackPokemonData, nextId: string): AttackPokemonData => ({
  ...profile,
  id: nextId,
  statInputs: { ...profile.statInputs },
  pokemon: JSON.parse(JSON.stringify(profile.pokemon)),
});

// 総合モードの「共通設定」は、防御/攻撃プロファイルの両方に反映する想定。
// 片方だけ作成して先に共通設定を変更した場合、後から作成したプロファイルに反映されないため、
// 作成時に既存プロファイルの共通項目を引き継ぐ。
const inheritComprehensiveCommonSettings = (source: AttackPokemonData, target: AttackPokemonData): AttackPokemonData => {
  const inherited: AttackPokemonData = {
    ...target,
    statInputs: { ...source.statInputs },
    selectedAbilityId: source.selectedAbilityId,
    selectedAbility: source.selectedAbility,
    selectedItemId: source.selectedItemId,
    terasuType: source.terasuType,
    terasuState: source.terasuState,
    isBurned: source.isBurned,
    isPoisoned: source.isPoisoned,
    isParalyzed: source.isParalyzed,
    kodai_quark_stat: source.kodai_quark_stat,
    attackRank: source.attackRank,
    defenseRank: source.defenseRank,
    specialAttackRank: source.specialAttackRank,
    specialDefenseRank: source.specialDefenseRank,
    speedRank: source.speedRank,
  };

  // rankCorrection は相手側の初期値（例: ザシアン等）を潰さないよう、target が未設定の時だけ引き継ぐ
  if (inherited.rankCorrection === undefined && source.rankCorrection !== undefined) {
    inherited.rankCorrection = source.rankCorrection;
  }

  return inherited;
};

type EffortField = keyof Pick<
  StatInputs,
  'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'
>;

const effortFieldMap: Record<EffortField, keyof PokemonV2['effective_slider_step']> = {
  hp_effort: 'hp',
  attack_effort: 'attack',
  defense_effort: 'defense',
  special_attack_effort: 'special_attack',
  special_defense_effort: 'special_defense',
  speed_effort: 'speed',
};

const sumEfforts = (inputs: StatInputs) =>
  inputs.hp_effort +
  inputs.attack_effort +
  inputs.defense_effort +
  inputs.special_attack_effort +
  inputs.special_defense_effort +
  inputs.speed_effort;

// 旧252基準の努力値が混在しているかを判定する
const hasLegacyEffortValues = (inputs: StatInputs) =>
  (Object.keys(effortFieldMap) as EffortField[]).some((field) => inputs[field] > MAX_STAT_EFFORT);

const clampEffortPoint = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAX_STAT_EFFORT, Math.floor(value)));
};

const legacyEffortToPoint = (value: number) => {
  if (value <= 0) return 0;
  if (value <= 4) return 1;
  return Math.min(MAX_STAT_EFFORT, Math.ceil((value - 4) / 8) + 1);
};

const normalizeEffortInputs = (inputs: StatInputs): StatInputs => {
  const converter = hasLegacyEffortValues(inputs) ? legacyEffortToPoint : clampEffortPoint;
  const normalized: StatInputs = { ...inputs };
  (Object.keys(effortFieldMap) as EffortField[]).forEach((field) => {
    normalized[field] = clampEffortWithTotal(normalized, field, converter(inputs[field]));
  });
  return normalized;
};

const clampEffortWithTotal = (_inputs: StatInputs, _statName: EffortField, rawValue: number) => {
  // 合計上限は超過許可のため、個別の上限のみを適用する
  return clampEffortPoint(rawValue);
};

type AttackEffortField = keyof Pick<
  AttackStatInputs,
  'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'
>;

const attackEffortFields: AttackEffortField[] = [
  'hp_effort',
  'attack_effort',
  'defense_effort',
  'special_attack_effort',
  'special_defense_effort',
  'speed_effort',
];

const attackEffortFieldSet = new Set<AttackEffortField>(attackEffortFields);

const sumAttackEfforts = (inputs: AttackStatInputs) =>
  inputs.hp_effort +
  inputs.attack_effort +
  inputs.defense_effort +
  inputs.special_attack_effort +
  inputs.special_defense_effort +
  inputs.speed_effort;

const clampAttackEffortWithTotal = (inputs: AttackStatInputs, statName: AttackEffortField, rawValue: number) => {
  const sanitized = clampEffortPoint(rawValue);
  const currentValue = inputs[statName];
  const otherTotal = sumAttackEfforts(inputs) - currentValue;
  const maxAllowed = Math.max(0, MAX_TOTAL_EFFORT - otherTotal);
  return Math.min(sanitized, maxAllowed);
};

const normalizeAttackStatInputs = (inputs: AttackStatInputs): AttackStatInputs => {
  const converter = sumAttackEfforts(inputs) > MAX_TOTAL_EFFORT ? legacyEffortToPoint : clampEffortPoint;
  const normalized: AttackStatInputs = { ...inputs };
  attackEffortFields.forEach((field) => {
    normalized[field] = clampAttackEffortWithTotal(normalized, field, converter(inputs[field]));
  });
  return normalized;
};

// `([])` が付与されたフォーム名は、生成時の不正データとみなして復元/候補から除外する
const isEmptyFormPokemonName = (name: unknown): boolean =>
  typeof name === 'string' && name.includes('([])');

// Props interface for save/load functions
interface PokemonSelectPageProps {
  onSave?: () => void;
  onLoad?: () => void;
  rightSidebarOpen: boolean;
  onToggleRightSidebar?: () => void;
  currentOtherSetting: otherSetting;
  onOtherSettingChange: (setting: otherSetting) => void;
  showSvOnly: boolean;
}

const PokemonSelectPage: React.FC<PokemonSelectPageProps> = ({ onSave, onLoad, rightSidebarOpen, onToggleRightSidebar, currentOtherSetting, onOtherSettingChange, showSvOnly }) => {
  // モード管理 ('defense' | 'attack' | 'speed')
  const [currentMode, setCurrentMode] = useState<'defense' | 'attack' | 'speed'>('defense');
  const { pokemonList } = usePokemonList();

  // 初期化完了フラグ
  const [isInitialized, setIsInitialized] = useState(false);

  // 左側のポケモン（全モード共通）
  const [selectedPokemon, setSelectedPokemon] = useState<PokemonV2 | null>(null);
  const [statInputs, setStatInputs] = useState<StatInputs>(initialStatInputs);
  const [selectedDefenseItem, setSelectedDefenseItem] = useState<DEFENCE_ITEM_ID>(DEFENCE_ITEM_ID_NO_SET);

  // 右側のポケモンリスト（モード別）
  const [attackPokemonList, setAttackPokemonList] = useState<AttackPokemonData[]>(() => {
    const result = attackListStorage.load();
    const stored = (result.success && result.data) ? result.data : [];
    // 旧データに `([])` が混入している場合、画面崩壊を避けるため破棄する
    const filtered = stored.filter((profile) => !isEmptyFormPokemonName(profile?.pokemon?.name_ja));
    return filtered.map((profile) => {
      const candidateItemId = profile.selectedItemId ?? profile.pokemon.selected_item_id;
      if (candidateItemId == null) return profile;
      if (canPokemonHoldItem(profile.pokemon.id, candidateItemId)) return profile;
      return {
        ...profile,
        selectedItemId: OFFENCE_ITEM_ID_NO_SET,
        pokemon: { ...profile.pokemon, selected_item_id: OFFENCE_ITEM_ID_NO_SET },
      };
    });
  });  // 攻撃モード用
  const [defencePokemonList, setDefencePokemonList] = useState<AttackPokemonData[]>(() => {
    const result = defenceListStorage.load();
    const stored = (result.success && result.data) ? result.data : [];
    // 旧データに `([])` が混入している場合、画面崩壊を避けるため破棄する
    const filtered = stored.filter((profile) => !isEmptyFormPokemonName(profile?.pokemon?.name_ja));
    return filtered.map((profile) => {
      const candidateItemId = profile.selectedItemId ?? profile.pokemon.selected_item_id;
      if (candidateItemId == null) return profile;
      if (canPokemonHoldItem(profile.pokemon.id, candidateItemId)) return profile;
      return {
        ...profile,
        selectedItemId: OFFENCE_ITEM_ID_NO_SET,
        pokemon: { ...profile.pokemon, selected_item_id: OFFENCE_ITEM_ID_NO_SET },
      };
    });
  });  // 防御モード用
  const [selectedAttackPokemonForMove, setSelectedAttackPokemonForMove] = useState<string | null>(null);

  // 総合モード用の相手リスト
  const [isComprehensiveMode, setIsComprehensiveMode] = useState(true);
  const [comprehensiveOpponents, setComprehensiveOpponents] = useState<OpponentCard[]>([]);
  const [selectedOpponentId, setSelectedOpponentId] = useState<string | null>(null);
  const [lastTouchedOpponentId, setLastTouchedOpponentId] = useState<string | null>(null);
  const [comprehensiveUiFlags, setComprehensiveUiFlags] = useState<ComprehensiveUiFlags>(
    DEFAULT_COMPREHENSIVE_UI_FLAGS,
  );
  const [isComprehensiveInitialized, setIsComprehensiveInitialized] = useState(false);
  // Toast message state - 複数のトーストを管理
  const [toasts, setToasts] = useState<Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    durationMs: number;
  }>>([]);

  // 最適化されたダメージ計算キャッシュ（サイズ制限付き）
  const damageCache = useMemo(() => {
    const cache = new Map<string, string>();
    const maxSize = 200; // キャッシュサイズ制限
    const originalSet = cache.set.bind(cache);
    cache.set = function (key: string, value: string) {
      if (this.size >= maxSize) {
        const firstKey = this.keys().next().value;
        if (firstKey !== undefined) {
          this.delete(firstKey);
        }
      }
      return originalSet(key, value);
    };
    return cache;
  }, []);

  // 詳細計算（retCalc）は別キャッシュで保持し、表示切替でも再計算を避ける
  const damageDetailCache = useMemo(() => {
    const cache = new Map<string, retCalc>();
    const maxSize = 200;
    const originalSet = cache.set.bind(cache);
    cache.set = function (key: string, value: retCalc) {
      if (this.size >= maxSize) {
        // FIFOで最古の結果を破棄してメモリ増加を防ぐ
        const firstKey = this.keys().next().value;
        if (firstKey !== undefined) {
          this.delete(firstKey);
        }
      }
      return originalSet(key, value);
    };
    return cache;
  }, []);


  // Show toast message
  const showToast = useCallback((
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success',
    durationMs?: number,
  ) => {
    const resolvedDurationMs = durationMs ?? (message === '計算終了' ? 1000 : 3000);
    const newToast = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      message,
      severity,
      durationMs: resolvedDurationMs,
    };
    setToasts(prev => [...prev, newToast]);
  }, []);

  // Close toast message
  const handleToastClose = (toastId: string) => (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  const resolveOpponentPokemon = useCallback((opponent: OpponentCard): PokemonV2 | null => {
    return opponent.defenseProfile?.pokemon ?? opponent.offenseProfile?.pokemon ?? null;
  }, []);

  const normalizeOpponentSpeedSettings = useCallback((settings?: OpponentSpeedSettings): OpponentSpeedSettings => {
    const base = createOpponentSpeedSettings();
    if (!settings) {
      return base;
    }
    return {
      self: { ...base.self, ...settings.self },
      opponent: { ...base.opponent, ...settings.opponent },
      isTrickRoom: settings.isTrickRoom ?? base.isTrickRoom,
    };
  }, []);

  const normalizeComprehensiveProfile = useCallback((profile?: AttackPokemonData): AttackPokemonData | undefined => {
    if (!profile) {
      return profile;
    }
    const normalizedStatInputs = normalizeAttackStatInputs({
      ...initialStatInputs,
      ...profile.statInputs,
    });
    const candidateItemId = profile.selectedItemId ?? profile.pokemon.selected_item_id;
    if (candidateItemId != null && !canPokemonHoldItem(profile.pokemon.id, candidateItemId)) {
      return {
        ...profile,
        statInputs: normalizedStatInputs,
        selectedItemId: OFFENCE_ITEM_ID_NO_SET,
        pokemon: { ...profile.pokemon, selected_item_id: OFFENCE_ITEM_ID_NO_SET },
      };
    }
    return {
      ...profile,
      statInputs: normalizedStatInputs,
    };
  }, []);

  const normalizeOpponentCard = useCallback((opponent: OpponentCard): OpponentCard => {
	    const assumedMoveIds = Array.from(new Set(opponent.assumedMoveIds ?? []));
	    const assumedMoves = getMovesByIds(assumedMoveIds);
	    const pokemon = resolveOpponentPokemon(opponent);
	    const baseName = opponent.name ?? pokemon?.name_ja ?? '';
	    const displayName = pokemon
	      ? formatPokemonDisplayName({ id: pokemon.id, name_ja: baseName })
	      : formatPokemonDisplayName(baseName);

	    return {
	      ...opponent,
      pokemonId: opponent.pokemonId ?? pokemon?.id ?? 0,
      speciesId: opponent.speciesId ?? pokemon?.species_id ?? 0,
      name: displayName,
      level: opponent.level ?? 50,
      formId: opponent.formId ?? null,
      otherSetting: opponent.otherSetting ?? currentOtherSetting,
      speedSettings: normalizeOpponentSpeedSettings(opponent.speedSettings),
      defenseProfile: normalizeComprehensiveProfile(opponent.defenseProfile),
      offenseProfile: normalizeComprehensiveProfile(opponent.offenseProfile),
      assumedMoveIds,
      assumedMoves,
    };
  }, [resolveOpponentPokemon, currentOtherSetting, normalizeComprehensiveProfile, normalizeOpponentSpeedSettings]);

  const buildOpponentsFromLegacy = useCallback(() => {
    const opponentMap = new Map<string, OpponentCard>();

    const upsertOpponent = (
      profile: AttackPokemonData,
      profileKey: 'defenseProfile' | 'offenseProfile',
    ) => {
      const pokemon = profile.pokemon;
      const key = `${pokemon.id}-${pokemon.species_id}`;
      const existing = opponentMap.get(key);
      const nextProfile = cloneAttackPokemonData(profile, `${profileKey}-${Date.now()}-${Math.random()}`);

      if (!existing) {
        const baseOpponent: OpponentCard = {
          id: `comprehensive-${pokemon.id}-${Date.now()}-${Math.random()}`,
          pokemonId: pokemon.id,
          speciesId: pokemon.species_id,
          formId: null,
          name: formatPokemonDisplayName(pokemon),
          level: 50,
          defenseProfile: profileKey === 'defenseProfile' ? nextProfile : undefined,
          offenseProfile: profileKey === 'offenseProfile' ? nextProfile : undefined,
          assumedMoveIds: [],
        };

        if (profileKey === 'offenseProfile') {
          const moveId = profile.selectedMoveId ?? profile.selectedMove?.id;
          if (moveId) {
            baseOpponent.assumedMoveIds = [moveId];
          }
        }

        opponentMap.set(key, baseOpponent);
        return;
      }

      const updated = { ...existing };
      if (profileKey === 'defenseProfile') {
        updated.defenseProfile = nextProfile;
      } else {
        updated.offenseProfile = nextProfile;
        const moveId = profile.selectedMoveId ?? profile.selectedMove?.id;
        if (moveId) {
          updated.assumedMoveIds = Array.from(new Set([...updated.assumedMoveIds, moveId]));
        }
      }

      opponentMap.set(key, updated);
    };

    attackPokemonList.forEach((profile) => upsertOpponent(profile, 'defenseProfile'));
    defencePokemonList.forEach((profile) => upsertOpponent(profile, 'offenseProfile'));

    const opponents = Array.from(opponentMap.values()).map(normalizeOpponentCard);
    const fallbackId = opponents[0]?.id ?? null;
    return {
      opponents,
      selectedOpponentId: fallbackId,
      lastTouchedOpponentId: fallbackId,
    };
  }, [attackPokemonList, defencePokemonList, normalizeOpponentCard]);

  // Save attack pokemon list to localStorage whenever it changes
  useEffect(() => {
    const result = attackListStorage.save(attackPokemonList);
    if (!result.success) {
      console.error('Error saving attack pokemon list to localStorage:', result.error);
    }
  }, [attackPokemonList]);

  // Save defence pokemon list to localStorage whenever it changes
  useEffect(() => {
    const result = defenceListStorage.save(defencePokemonList);
    if (!result.success) {
      console.error('Error saving defence pokemon list to localStorage:', result.error);
    }
  }, [defencePokemonList]);

  // 総合モードの保存は初期化完了後にのみ実行する
  useEffect(() => {
    if (!isComprehensiveInitialized) {
      return;
    }

    const payload = {
      opponents: comprehensiveOpponents.map((opponent) => ({
        ...opponent,
        assumedMoves: undefined,
      })),
      selectedOpponentId,
      lastTouchedOpponentId,
      uiFlags: comprehensiveUiFlags,
      globalConditions: currentOtherSetting,
    };

    const result = comprehensiveListStorage.save(payload);
    if (!result.success) {
      console.error('Error saving comprehensive opponent list:', result.error);
    }
  }, [
    comprehensiveOpponents,
    selectedOpponentId,
    lastTouchedOpponentId,
    comprehensiveUiFlags,
    currentOtherSetting,
    isComprehensiveInitialized,
  ]);


  const excludedPokemonIdSet = useMemo(() => new Set(EXCLUDED_POKEMON_IDS), []);

  // Memoized filtered Pokemon list for autocomplete
  const pokemonOptions: PokemonOption[] = useMemo(() => {
    const availablePokemonList = pokemonList.filter((pokemon) => !excludedPokemonIdSet.has(pokemon.id));
    // SVのみ表示でも「ピカチュウ(通常)」は選択できるようにする（マスターデータ側の is_sv が未整備でも使えるようにする）
    const filteredPokemonList = showSvOnly
      ? availablePokemonList.filter((pokemon) => pokemon.is_sv === true || pokemon.id === 25)
      : availablePokemonList;
    return filteredPokemonList.map(pokemon => ({
      label: formatPokemonDisplayName(pokemon),
      value: pokemon.id,
      pokemon: pokemon
    }));
  }, [excludedPokemonIdSet, pokemonList, showSvOnly]);

  // 旧localStorage復元などで「今の候補に存在するか」を判定するためのIDセット
  const pokemonOptionIdSet = useMemo(() => new Set(pokemonOptions.map((option) => option.value)), [pokemonOptions]);

  const canRestorePokemon = useCallback((pokemon: PokemonV2 | null | undefined): pokemon is PokemonV2 => {
    if (!pokemon) {
      return false;
    }
    if (isEmptyFormPokemonName(pokemon.name_ja)) {
      return false;
    }
    return pokemonOptionIdSet.has(pokemon.id);
  }, [pokemonOptionIdSet]);

  // 右側リストも、候補に存在しない/空フォームのポケモンは復元時に破棄する
  useEffect(() => {
    if (pokemonOptionIdSet.size === 0) {
      return;
    }

    setAttackPokemonList((prev) => {
      const next = prev.filter((profile) => canRestorePokemon(profile.pokemon));
      return next.length === prev.length ? prev : next;
    });
    setDefencePokemonList((prev) => {
      const next = prev.filter((profile) => canRestorePokemon(profile.pokemon));
      return next.length === prev.length ? prev : next;
    });
  }, [canRestorePokemon, pokemonOptionIdSet.size]);

  const handlePokemonSelect = useCallback((event: any, newValue: any) => {
    if (newValue && newValue.pokemon) {
      const foundPokemon = newValue.pokemon;
      const defenceVersion = toDefence(JSON.parse(JSON.stringify(foundPokemon)));

      // Auto-select the most frequently used ability
      if (defenceVersion.ability_list && defenceVersion.ability_list.length > 0) {
        // Use the first ability from often_used_tokusei array (highest usage rate)
        if (defenceVersion.often_used_tokusei && defenceVersion.often_used_tokusei.length > 0) {
          const mostUsedAbilityId = defenceVersion.often_used_tokusei[0];
          const mostUsedAbility = defenceVersion.ability_list.find(ability => ability.id === mostUsedAbilityId);
          if (mostUsedAbility) {
            defenceVersion.selected_ability_id = mostUsedAbility.id;
            defenceVersion.selected_ability = mostUsedAbility;
          }
        } else {
          // Fallback to first ability if often_used_tokusei is not available
          defenceVersion.selected_ability_id = defenceVersion.ability_list[0].id;
          defenceVersion.selected_ability = defenceVersion.ability_list[0];
        }
      }

      // Set the defense item to current selected value, or auto-set for specific Pokemon
      let itemToSet = selectedDefenseItem;

      const megaStoneItemId = getMegaStoneItemIdForPokemon(foundPokemon);
      if (megaStoneItemId != null) {
        itemToSet = megaStoneItemId;
        setSelectedDefenseItem(megaStoneItemId);
      } else if (isMegaStoneItemId(itemToSet)) {
        // メガ以外へ持ち越されたメガストーンは解除する（手動選択できないため）
        itemToSet = DEFENCE_ITEM_ID_NO_SET;
        setSelectedDefenseItem(DEFENCE_ITEM_ID_NO_SET);
      }

      // ゲノセクト以外に持ち越されたカセット系は解除する（手動で選べないようにするため）
      if (!canPokemonHoldItem(foundPokemon.id, itemToSet)) {
        itemToSet = DEFENCE_ITEM_ID_NO_SET;
        setSelectedDefenseItem(DEFENCE_ITEM_ID_NO_SET);
      }

      // Auto-set specific items for Origin Forme Pokemon
      if (megaStoneItemId == null) {
        if (foundPokemon.id === 10245) { // Dialga (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAIKONGOUDAMA;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_DAIKONGOUDAMA);
        } else if (foundPokemon.id === 10246) { // Palkia (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAISHIRATAMA;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_DAISHIRATAMA);
        } else if (foundPokemon.id === 10007) { // Giratina (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAIHAKKINDAMA;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_DAIHAKKINDAMA);
        } else if (foundPokemon.id === 10077) { // Kyogre (Primal)
          itemToSet = DEFENCE_ITEM_ID_AIIRO_NO_TAMA;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_AIIRO_NO_TAMA);
        } else if (foundPokemon.id === 10078) { // Groudon (Primal)
          itemToSet = DEFENCE_ITEM_ID_BENIIRO_NO_TAMA;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_BENIIRO_NO_TAMA);
        } else if (foundPokemon.id === 10188) { // Zacian (Crowned Sword)
          itemToSet = DEFENCE_ITEM_ID_RUSTED_SWORD;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_RUSTED_SWORD);
        } else if (foundPokemon.id === 10189) { // Zamazenta (Crowned Shield)
          itemToSet = DEFENCE_ITEM_ID_RUSTED_SHIELD;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_RUSTED_SHIELD);
        } else if (foundPokemon.id === 10273) { // Ogerpon (Well Mask)
          itemToSet = DEFENCE_ITEM_ID_IDONOMEN;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_IDONOMEN);
        } else if (foundPokemon.id === 10274) { // Ogerpon (Hearthflame Mask)
          itemToSet = DEFENCE_ITEM_ID_KAMADONOMEN;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_KAMADONOMEN);
        } else if (foundPokemon.id === 10275) { // Ogerpon (Cornerstone Mask)
          itemToSet = DEFENCE_ITEM_ID_ISHIZUENOMEN;
          setSelectedDefenseItem(DEFENCE_ITEM_ID_ISHIZUENOMEN);
        } else {
	          const arceusPlateItem = getPlateItemIdForArceusPokemon(foundPokemon);
	          if (arceusPlateItem) {
	            itemToSet = arceusPlateItem as DEFENCE_ITEM_ID;
	            setSelectedDefenseItem(arceusPlateItem as DEFENCE_ITEM_ID);
	          } else if (shouldAutoSetThickClub(foundPokemon)) {
	            itemToSet = OFFENCE_ITEM_ID_FUTOIHONE;
	            setSelectedDefenseItem(OFFENCE_ITEM_ID_FUTOIHONE);
	          } else if (shouldAutoSetLightBall(foundPokemon)) {
	            itemToSet = OFFENCE_ITEM_ID_DENKIDAMA;
	            setSelectedDefenseItem(OFFENCE_ITEM_ID_DENKIDAMA);
	          } else if (shouldAutoSetEviolite(foundPokemon.id)) {
	            // Always set Eviolite for Pokemon that benefit from it (but not fixed)
	            itemToSet = DEFENCE_ITEM_ID_SINKANOKISEKI;
	            setSelectedDefenseItem(DEFENCE_ITEM_ID_SINKANOKISEKI);
	          }
	        }
	      }

      defenceVersion.selected_item_id = itemToSet;

      // 努力値と性格補正を初期化（effective_slider_stepも同期）
      defenceVersion.effective_slider_step = {
        hp: 0,
        attack: 0,
        defense: 0,
        special_attack: 0,
        special_defense: 0,
        speed: 0
      };

      defenceVersion.personality = {
        attack: 1.0,
        defense: 1.0,
        special_attack: 1.0,
        special_defense: 1.0,
        speed: 1.0
      };

      setSelectedPokemon(defenceVersion);

      setStatInputs({
        hp_effort: 0,
        attack_effort: 0,
        defense_effort: 0,
        special_attack_effort: 0,
        special_defense_effort: 0,
        speed_effort: 0,
        attack_personality: 1.0,
        defense_personality: 1.0,
        special_attack_personality: 1.0,
        special_defense_personality: 1.0,
        speed_personality: 1.0,
      });
    } else {
      setSelectedPokemon(null);
      setStatInputs(initialStatInputs);
      setSelectedDefenseItem(DEFENCE_ITEM_ID_NO_SET);
    }
  }, [selectedDefenseItem]);

  // 技選択のハンドラー関数
  const handleMoveSelect = useCallback((moveIndex: number, move: Move | null) => {
    if (selectedPokemon) {
      const currentMoves = selectedPokemon.selected_moves || [];
      const currentMoveIds = selectedPokemon.selected_move_ids || [];

      // 新しい技リストを作成（最大4つまで）
      const newMoves = [...currentMoves];
      const newMoveIds = [...currentMoveIds];

      // 配列を4つのスロットに拡張
      while (newMoves.length < 4) {
        newMoves.push(null as any);
        newMoveIds.push(-1);
      }

      if (move === null) {
        // 技を削除
        newMoves[moveIndex] = null as any;
        newMoveIds[moveIndex] = -1;
      } else {
        // 技を設定
        newMoves[moveIndex] = move;
        newMoveIds[moveIndex] = move.id;
      }

      // null要素を除去
      const filteredMoves = newMoves.filter(m => m !== null);
      const filteredMoveIds = newMoveIds.filter(id => id !== -1);

      setSelectedPokemon(prev => prev ? {
        ...prev,
        selected_moves: filteredMoves,
        selected_move_ids: filteredMoveIds
      } : null);
    }
  }, [selectedPokemon]);

  // const handleMoveRemove = useCallback((moveIndex: number) => {
  //   handleMoveSelect(moveIndex, null);
  // }, [handleMoveSelect]);

  // 利用可能な技のリストを取得（物理・特殊・変化技すべて含む）
  const availableMoves = useMemo(() => {
    if (!selectedPokemon) return [];

    return getMovesByIds(selectedPokemon.move_id_list).sort((a, b) =>
      a.name_ja.localeCompare(b.name_ja)
    );
  }, [selectedPokemon]);

  // Load saved Pokemon data on initial mount
  useEffect(() => {
    // 初期化が完了済みの場合は実行しない
    if (isInitialized) return;

    const loadInitialData = () => {
      try {
        const savedData = localStorage.getItem('pokemonSelectPageData');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.otherSetting) {
            onOtherSettingChange(parsedData.otherSetting);
          }

          const loadedPokemon = parsedData.selectedPokemon as PokemonV2 | null | undefined;
          // `([])` 等の不正ポケモンは復元しない（デフォルト選択へフォールバックさせる）
          if (loadedPokemon && !canRestorePokemon(loadedPokemon)) {
            return false;
          }

          if (loadedPokemon) {
            const candidateItemId = loadedPokemon.selected_item_id as DEFENCE_ITEM_ID | undefined;
            const normalizedItemId =
              candidateItemId != null && !canPokemonHoldItem(loadedPokemon.id, candidateItemId)
                ? DEFENCE_ITEM_ID_NO_SET
                : candidateItemId;
            setSelectedPokemon(
              normalizedItemId !== candidateItemId
                ? { ...loadedPokemon, selected_item_id: normalizedItemId }
                : loadedPokemon,
            );
          }
          if (parsedData.statInputs) {
            setStatInputs(normalizeEffortInputs(parsedData.statInputs));
          }
          if (parsedData.selectedDefenseItem) {
            const candidateItemId = parsedData.selectedDefenseItem as DEFENCE_ITEM_ID;
            const normalizedItemId =
              loadedPokemon && !canPokemonHoldItem(loadedPokemon.id, candidateItemId)
                ? DEFENCE_ITEM_ID_NO_SET
                : candidateItemId;
            setSelectedDefenseItem(normalizedItemId);
          }

          return true; // データが読み込まれた
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      }
      return false; // データが読み込まれなかった
    };

    // pokemonOptionsが読み込まれてから実行
    if (pokemonOptions.length > 0) {
      // 保存データの読み込みを試行
      const dataLoaded = loadInitialData();

      // 保存データがない場合のみデフォルトポケモンを設定
      if (!dataLoaded && !selectedPokemon) {
        const defaultPokemon = pokemonOptions[0]; // First Pokemon is the most popular
        handlePokemonSelect(null, defaultPokemon);
      }

      // 初期化完了をマーク
      setIsInitialized(true);
    }
  }, [pokemonOptions, selectedPokemon, handlePokemonSelect, onOtherSettingChange, isInitialized, canRestorePokemon]);

  // 総合モードの初期化（保存データ or レガシーからの移行）
  useEffect(() => {
    if (isComprehensiveInitialized) {
      return;
    }
    if (pokemonOptions.length === 0) {
      return;
    }

    const stored = comprehensiveListStorage.load();
    if (stored.success && stored.data) {
      const storedData = stored.data;
      const baseOtherSetting = storedData.globalConditions ?? currentOtherSetting;
      // `([])` 等の不正ポケモンが保存されている場合、復元時に破棄して安全に復元する
      const validStoredOpponents = storedData.opponents.filter((opponent) => {
        const pokemon = resolveOpponentPokemon(opponent);
        return pokemon != null && canRestorePokemon(pokemon);
      });
      const normalizedOpponents = validStoredOpponents.map((opponent) =>
        normalizeOpponentCard({
          ...opponent,
          otherSetting: opponent.otherSetting ?? baseOtherSetting,
        }),
      );

      if (normalizedOpponents.length > 0) {
        setComprehensiveOpponents(normalizedOpponents);
        setComprehensiveUiFlags(storedData.uiFlags ?? DEFAULT_COMPREHENSIVE_UI_FLAGS);

        const lastTouchedId = storedData.lastTouchedOpponentId ?? null;
        const validLastTouched = normalizedOpponents.find((opponent) => opponent.id === lastTouchedId);
        const validSelected = normalizedOpponents.find((opponent) => opponent.id === storedData.selectedOpponentId);
        const fallbackId = validLastTouched?.id ?? validSelected?.id ?? normalizedOpponents[0]?.id ?? null;
        setSelectedOpponentId(fallbackId);
        setLastTouchedOpponentId(fallbackId);

        if (storedData.globalConditions) {
          onOtherSettingChange(storedData.globalConditions);
        }
      } else {
        // 全件が無効ならレガシーから再構築する
        const migrated = buildOpponentsFromLegacy();
        setComprehensiveOpponents(migrated.opponents);
        setSelectedOpponentId(migrated.selectedOpponentId);
        setLastTouchedOpponentId(migrated.lastTouchedOpponentId);
      }
    } else {
      const migrated = buildOpponentsFromLegacy();
      setComprehensiveOpponents(migrated.opponents);
      setSelectedOpponentId(migrated.selectedOpponentId);
      setLastTouchedOpponentId(migrated.lastTouchedOpponentId);
    }

    setIsComprehensiveInitialized(true);
  }, [
    buildOpponentsFromLegacy,
    currentOtherSetting,
    isComprehensiveInitialized,
    normalizeOpponentCard,
    onOtherSettingChange,
    pokemonOptions.length,
    canRestorePokemon,
    resolveOpponentPokemon,
  ]);


  // 防御モード専用：技選択ありの攻撃側ポケモン追加関数
  const handleAddAttackPokemon = (event: any, newValue: any) => {
    if (newValue && newValue.pokemon) {
      const foundPokemon = newValue.pokemon;
      const defenceVersion = toDefence(JSON.parse(JSON.stringify(foundPokemon)));

      // Auto-set ability if only one exists
      let selectedAbilityId: number | undefined;
      let selectedAbility: Ability | undefined;

      if (defenceVersion.ability_list && defenceVersion.ability_list.length > 0) {
        selectedAbilityId = defenceVersion.ability_list[0].id;
        selectedAbility = defenceVersion.ability_list[0];
      }

      // Set initial rank correction and item for specific Pokemon
      let initialRankCorrection: rankCorrectionEnum = 0;
      let selectedItemId: OFFENCE_ITEM_ID | undefined;

      const megaStoneItemId = getMegaStoneItemIdForPokemon(foundPokemon);
      if (megaStoneItemId != null) {
        selectedItemId = megaStoneItemId;
      } else if (foundPokemon.id === 10187 || foundPokemon.id === 10188) { // Zacian (Hero/Crowned)
        initialRankCorrection = 1; // +1 attack rank
        if (foundPokemon.id === 10188) { // Zacian (Crowned Sword) gets rusted sword
          selectedItemId = OFFENCE_ITEM_ID_RUSTED_SWORD;
        }
      } else if (foundPokemon.id === 10189) { // Zamazenta (Crowned Shield)
        selectedItemId = OFFENCE_ITEM_ID_RUSTED_SHIELD;
      } else if (foundPokemon.id === 10245) { // Dialga (Origin Forme)
        selectedItemId = OFFENCE_ITEM_ID_DAIKONGOUDAMA;
      } else if (foundPokemon.id === 10246) { // Palkia (Origin Forme)
        selectedItemId = OFFENCE_ITEM_ID_DAISHIRATAMA;
      } else if (foundPokemon.id === 10007) { // Giratina (Origin Forme)
        selectedItemId = OFFENCE_ITEM_ID_DAIHAKKINDAMA;
      } else if (foundPokemon.id === 10077) { // Kyogre (Primal)
        selectedItemId = OFFENCE_ITEM_ID_AIIRO_NO_TAMA;
	      } else if (foundPokemon.id === 10078) { // Groudon (Primal)
	        selectedItemId = OFFENCE_ITEM_ID_BENIIRO_NO_TAMA;
	      } else if (foundPokemon.id === 10273) { // Ogerpon (Well Mask)
	        selectedItemId = OFFENCE_ITEM_ID_IDONOMEN;
	      } else if (foundPokemon.id === 10274) { // Ogerpon (Hearthflame Mask)
	        selectedItemId = OFFENCE_ITEM_ID_KAMADONOMEN;
	      } else if (foundPokemon.id === 10275) { // Ogerpon (Cornerstone Mask)
	        selectedItemId = OFFENCE_ITEM_ID_ISHIZUENOMEN;
	      } else if (shouldAutoSetThickClub(foundPokemon)) {
	        selectedItemId = OFFENCE_ITEM_ID_FUTOIHONE;
	      } else if (shouldAutoSetLightBall(foundPokemon)) {
	        selectedItemId = OFFENCE_ITEM_ID_DENKIDAMA;
	      }

      const arceusPlateForAttack = getPlateItemIdForArceusPokemon(foundPokemon);
      if (megaStoneItemId == null && arceusPlateForAttack) {
        selectedItemId = arceusPlateForAttack as OFFENCE_ITEM_ID;
      }

      // テラパゴス(ステラフォルム)の場合はテラスタイプをステラ固定
      let terasuType: number | undefined;
      if (foundPokemon.id === 10277) { // テラパゴス(ステラフォルム)
        terasuType = 19; // ステラタイプ
      }

      if (selectedItemId) {
        defenceVersion.selected_item_id = selectedItemId;
      }

      const newAttackPokemon: AttackPokemonData = {
        pokemon: defenceVersion,
        statInputs: { ...initialAttackStatInputs },
        id: `defense-attack-${Date.now()}-${Math.random()}`,
        selectedAbilityId,
        selectedAbility,
        selectedItemId,
        rankCorrection: initialRankCorrection,
        isBurned: false,
        isPoisoned: false,
        isParalyzed: false,
        terasuType
      };

      // 防御モードでは攻撃側ポケモンリストに追加
      setDefencePokemonList(prev => [...prev, newAttackPokemon]);

      // Automatically select this Pokemon for move selection (since it's new, it won't have a move)
      setSelectedAttackPokemonForMove(newAttackPokemon.id);
    }
  };

  // 攻撃モード専用：技選択なしの防御側ポケモン追加関数
  const handleAddDefenseTargetPokemon = (event: any, newValue: any) => {
    if (newValue && newValue.pokemon) {
      const foundPokemon = newValue.pokemon;
      const defenceVersion = toDefence(JSON.parse(JSON.stringify(foundPokemon)));

      // Auto-select the most frequently used ability
      if (defenceVersion.ability_list && defenceVersion.ability_list.length > 0) {
        // Use the first ability from often_used_tokusei array (highest usage rate)
        if (defenceVersion.often_used_tokusei && defenceVersion.often_used_tokusei.length > 0) {
          const mostUsedAbilityId = defenceVersion.often_used_tokusei[0];
          const mostUsedAbility = defenceVersion.ability_list.find(ability => ability.id === mostUsedAbilityId);
          if (mostUsedAbility) {
            defenceVersion.selected_ability_id = mostUsedAbility.id;
            defenceVersion.selected_ability = mostUsedAbility;
          }
        } else {
          // Fallback to first ability if often_used_tokusei is not available
          defenceVersion.selected_ability_id = defenceVersion.ability_list[0].id;
          defenceVersion.selected_ability = defenceVersion.ability_list[0];
        }
      }

      // Set default defense item based on Pokemon
      let itemToSet = DEFENCE_ITEM_ID_NO_SET;

      const megaStoneItemId = getMegaStoneItemIdForPokemon(foundPokemon);
      if (megaStoneItemId != null) {
        itemToSet = megaStoneItemId;
      }

      // Auto-set specific items for Origin Forme Pokemon
      if (megaStoneItemId == null) {
        if (foundPokemon.id === 10245) { // Dialga (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAIKONGOUDAMA;
        } else if (foundPokemon.id === 10246) { // Palkia (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAISHIRATAMA;
        } else if (foundPokemon.id === 10007) { // Giratina (Origin Forme)
          itemToSet = DEFENCE_ITEM_ID_DAIHAKKINDAMA;
        } else if (foundPokemon.id === 10077) { // Kyogre (Primal)
          itemToSet = DEFENCE_ITEM_ID_AIIRO_NO_TAMA;
        } else if (foundPokemon.id === 10078) { // Groudon (Primal)
          itemToSet = DEFENCE_ITEM_ID_BENIIRO_NO_TAMA;
        } else if (foundPokemon.id === 10188) { // Zacian (Crowned Sword)
          itemToSet = DEFENCE_ITEM_ID_RUSTED_SWORD;
        } else if (foundPokemon.id === 10189) { // Zamazenta (Crowned Shield)
          itemToSet = DEFENCE_ITEM_ID_RUSTED_SHIELD;
        } else if (foundPokemon.id === 10273) { // Ogerpon (Well Mask)
          itemToSet = DEFENCE_ITEM_ID_IDONOMEN;
	        } else if (foundPokemon.id === 10274) { // Ogerpon (Hearthflame Mask)
	          itemToSet = DEFENCE_ITEM_ID_KAMADONOMEN;
	        } else if (foundPokemon.id === 10275) { // Ogerpon (Cornerstone Mask)
	          itemToSet = DEFENCE_ITEM_ID_ISHIZUENOMEN;
	        } else if (shouldAutoSetThickClub(foundPokemon)) {
	          itemToSet = OFFENCE_ITEM_ID_FUTOIHONE;
	        } else if (shouldAutoSetLightBall(foundPokemon)) {
	          itemToSet = OFFENCE_ITEM_ID_DENKIDAMA;
	        } else if (shouldAutoSetEviolite(foundPokemon.id)) {
	          // Always set Eviolite for Pokemon that benefit from it
	          itemToSet = DEFENCE_ITEM_ID_SINKANOKISEKI;
	        } else {
	          const arceusDefensePlate = getPlateItemIdForArceusPokemon(foundPokemon);
          if (arceusDefensePlate) {
            itemToSet = arceusDefensePlate as DEFENCE_ITEM_ID;
          }
        }
      }

      defenceVersion.selected_item_id = itemToSet;

      // 初期努力値と性格補正を設定
      defenceVersion.effective_slider_step = {
        hp: 0,
        attack: 0,
        defense: 0,
        special_attack: 0,
        special_defense: 0,
        speed: 0
      };

      defenceVersion.personality = {
        attack: 1.0,
        defense: 1.0,
        special_attack: 1.0,
        special_defense: 1.0,
        speed: 1.0
      };

      const newDefenseTargetPokemon: AttackPokemonData = {
        pokemon: defenceVersion,
        statInputs: { ...initialStatInputs },
        id: `attack-defense-${Date.now()}-${Math.random()}`,
        selectedAbilityId: defenceVersion.selected_ability_id,
        selectedAbility: defenceVersion.selected_ability,
        selectedItemId: megaStoneItemId ?? undefined, // メガストーンは固定表示のため保持しておく
        rankCorrection: 0,
        isBurned: false,
        isPoisoned: false,
        isParalyzed: false,
        terasuType: undefined
      };

      // 攻撃モードでは攻撃側ポケモンリスト（実質防御対象）に追加
      setAttackPokemonList(prev => [...prev, newDefenseTargetPokemon]);

      // 防御側ポケモンなので技選択は自動で行わない
    }
  };

  // 総合モード用：相手カード追加
  const handleAddComprehensiveOpponent = (event: any, newValue: any) => {
    if (!newValue || !newValue.pokemon) {
      return;
    }

    const foundPokemon = newValue.pokemon as PokemonV2;
    const defenseProfile = createComprehensiveDefenseProfile(foundPokemon);
    const offenseProfile = createComprehensiveOffenseProfile(foundPokemon);
    const assumedMoveIds = getDefaultOpponentAssumedMoveIds(foundPokemon);

    const opponent: OpponentCard = normalizeOpponentCard({
      id: `comprehensive-${Date.now()}-${Math.random()}`,
      pokemonId: foundPokemon.id,
      speciesId: foundPokemon.species_id,
      formId: null,
      name: foundPokemon.name_ja,
      level: 50,
      defenseProfile,
      offenseProfile,
      otherSetting: createEmptyOtherSetting(),
      speedSettings: createOpponentSpeedSettings(),
      assumedMoveIds,
    });

    setComprehensiveOpponents((prev) => [...prev, opponent]);
    setSelectedOpponentId(opponent.id);
    setLastTouchedOpponentId(opponent.id);
  };

  const handleSelectComprehensiveOpponent = (opponentId: string) => {
    setSelectedOpponentId(opponentId);
    setLastTouchedOpponentId(opponentId);
  };

  const handleRemoveComprehensiveOpponent = (opponentId: string) => {
    setComprehensiveOpponents((prev) => {
      const index = prev.findIndex((opponent) => opponent.id === opponentId);
      const next = prev.filter((opponent) => opponent.id !== opponentId);

      if (selectedOpponentId === opponentId) {
        const fallback = next[index] ?? next[index - 1] ?? null;
        setSelectedOpponentId(fallback?.id ?? null);
        setLastTouchedOpponentId(fallback?.id ?? null);
      }

      return next;
    });
  };

  const handleCopyComprehensiveOpponent = (opponentId: string) => {
    let copiedId: string | null = null;
    setComprehensiveOpponents((prev) => {
      const index = prev.findIndex((opponent) => opponent.id === opponentId);
      const target = prev[index];
      if (!target) {
        return prev;
      }

      const copied: OpponentCard = normalizeOpponentCard({
        ...target,
        id: `comprehensive-copy-${Date.now()}-${Math.random()}`,
        defenseProfile: target.defenseProfile
          ? cloneAttackPokemonData(target.defenseProfile, `comprehensive-defense-${Date.now()}-${Math.random()}`)
          : undefined,
        offenseProfile: target.offenseProfile
          ? cloneAttackPokemonData(target.offenseProfile, `comprehensive-offense-${Date.now()}-${Math.random()}`)
          : undefined,
        speedSettings: normalizeOpponentSpeedSettings(target.speedSettings),
      });
      copiedId = copied.id;

      const next = [...prev];
      next.splice(index + 1, 0, copied);
      return next;
    });
    if (copiedId) {
      setSelectedOpponentId(copiedId);
      setLastTouchedOpponentId(copiedId);
    }
  };

  const updateComprehensiveOpponent = useCallback((opponentId: string, updater: (opponent: OpponentCard) => OpponentCard) => {
    setComprehensiveOpponents((prev) =>
      prev.map((opponent) => {
        if (opponent.id !== opponentId) {
          return opponent;
        }
        return normalizeOpponentCard(updater(opponent));
      })
    );
    setLastTouchedOpponentId(opponentId);
  }, [normalizeOpponentCard]);

  const handleUpdateComprehensiveOtherSetting = useCallback((opponentId: string, setting: otherSetting) => {
    updateComprehensiveOpponent(opponentId, (opponent) => ({
      ...opponent,
      otherSetting: setting,
    }));
    damageCache.clear();
    damageDetailCache.clear();
  }, [updateComprehensiveOpponent, damageCache, damageDetailCache]);

  const handleUpdateComprehensiveSpeedSettings = useCallback((opponentId: string, settings: OpponentSpeedSettings) => {
    updateComprehensiveOpponent(opponentId, (opponent) => ({
      ...opponent,
      speedSettings: normalizeOpponentSpeedSettings(settings),
    }));
  }, [updateComprehensiveOpponent, normalizeOpponentSpeedSettings]);

  const updateComprehensiveProfileById = useCallback((profileId: string, updater: (profile: AttackPokemonData) => AttackPokemonData) => {
    let touchedOpponentId: string | null = null;
    setComprehensiveOpponents((prev) =>
      prev.map((opponent) => {
        let nextOpponent = opponent;
        if (opponent.defenseProfile && opponent.defenseProfile.id === profileId) {
          nextOpponent = { ...nextOpponent, defenseProfile: updater(opponent.defenseProfile) };
          touchedOpponentId = opponent.id;
        }
        if (opponent.offenseProfile && opponent.offenseProfile.id === profileId) {
          nextOpponent = { ...nextOpponent, offenseProfile: updater(opponent.offenseProfile) };
          touchedOpponentId = opponent.id;
        }
        if (nextOpponent === opponent) {
          return opponent;
        }
        return normalizeOpponentCard(nextOpponent);
      })
    );
    if (touchedOpponentId) {
      setLastTouchedOpponentId(touchedOpponentId);
    }
    damageCache.clear();
    damageDetailCache.clear();
  }, [normalizeOpponentCard, damageCache, damageDetailCache]);

  const handleUpdateComprehensiveAssumedMove = useCallback((opponentId: string, moveIndex: number, move: Move | null) => {
    updateComprehensiveOpponent(opponentId, (opponent) => {
      const currentMoveIds = opponent.assumedMoveIds ?? [];
      const newMoveIds = [...currentMoveIds];

      while (newMoveIds.length < 4) {
        newMoveIds.push(-1);
      }

      if (move === null) {
        newMoveIds[moveIndex] = -1;
      } else {
        newMoveIds[moveIndex] = move.id;
      }

      const filteredMoveIds = newMoveIds.filter((id) => id !== -1);

      return {
        ...opponent,
        assumedMoveIds: filteredMoveIds,
      };
    });
    damageCache.clear();
    damageDetailCache.clear();
  }, [updateComprehensiveOpponent, damageCache, damageDetailCache]);

  const handleCreateComprehensiveDefenseProfile = useCallback((opponentId: string) => {
    updateComprehensiveOpponent(opponentId, (opponent) => {
      if (opponent.defenseProfile) {
        return opponent;
      }
      const pokemon = resolveOpponentPokemon(opponent);
      if (!pokemon) {
        return opponent;
      }
      return {
        ...opponent,
        defenseProfile: opponent.offenseProfile
          ? inheritComprehensiveCommonSettings(opponent.offenseProfile, createComprehensiveDefenseProfile(pokemon))
          : createComprehensiveDefenseProfile(pokemon),
      };
    });
    damageCache.clear();
    damageDetailCache.clear();
  }, [resolveOpponentPokemon, updateComprehensiveOpponent, damageCache, damageDetailCache]);

  const handleCreateComprehensiveOffenseProfile = useCallback((opponentId: string) => {
    updateComprehensiveOpponent(opponentId, (opponent) => {
      if (opponent.offenseProfile) {
        return opponent;
      }
      const pokemon = resolveOpponentPokemon(opponent);
      if (!pokemon) {
        return opponent;
      }
      return {
        ...opponent,
        offenseProfile: opponent.defenseProfile
          ? inheritComprehensiveCommonSettings(opponent.defenseProfile, createComprehensiveOffenseProfile(pokemon))
          : createComprehensiveOffenseProfile(pokemon),
      };
    });
    damageCache.clear();
    damageDetailCache.clear();
  }, [resolveOpponentPokemon, updateComprehensiveOpponent, damageCache, damageDetailCache]);

  const handleUpdateComprehensiveProfileStats = useCallback((id: string, newStatInputsOrField: AttackStatInputs | string, value?: number) => {
    if (typeof newStatInputsOrField === 'string' && value !== undefined) {
      const field = newStatInputsOrField;
      updateComprehensiveProfileById(id, (profile) => {
        const completeStatInputs = normalizeAttackStatInputs({
          ...initialStatInputs,
          ...profile.statInputs,
        });
        let nextValue = value;
        if (attackEffortFieldSet.has(field as AttackEffortField)) {
          nextValue = clampAttackEffortWithTotal(
            completeStatInputs as AttackStatInputs,
            field as AttackEffortField,
            value,
          );
        }
        return {
          ...profile,
          statInputs: {
            ...completeStatInputs,
            [field]: nextValue,
          },
        };
      });
    } else if (typeof newStatInputsOrField === 'object') {
      const normalizedStatInputs = normalizeAttackStatInputs(newStatInputsOrField);
      updateComprehensiveProfileById(id, (profile) => ({
        ...profile,
        statInputs: normalizedStatInputs,
      }));
    }
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfilePersonality = useCallback((id: string, field: string, value: number) => {
    updateComprehensiveProfileById(id, (profile) => ({
      ...profile,
      statInputs: {
        ...profile.statInputs,
        [field]: value,
      },
    }));
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfileRank = useCallback((id: string, field: string, value: number) => {
    updateComprehensiveProfileById(id, (profile) => ({
      ...profile,
      [field]: value,
    }));
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfileAbility = useCallback((id: string, abilityId: number) => {
    updateComprehensiveProfileById(id, (profile) => {
      const selectedAbility = abilityId === 0 ? undefined : ability_list.find((ability) => ability.id === abilityId);
      return { ...profile, selectedAbilityId: abilityId, selectedAbility };
    });
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfileItem = useCallback((id: string, itemId: OFFENCE_ITEM_ID) => {
    updateComprehensiveProfileById(id, (profile) => {
      const lockedPlateItem = getPlateItemIdForArceusPokemon(profile.pokemon);
      if (lockedPlateItem && lockedPlateItem !== itemId) {
        return profile;
      }
      return { ...profile, selectedItemId: itemId };
    });
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfileTera = useCallback((id: string, terasuType: number) => {
    updateComprehensiveProfileById(id, (profile) => ({
      ...profile,
      terasuType,
    }));
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveKodaiQuarkStat = useCallback((id: string, value: 'attack' | 'special_attack') => {
    updateComprehensiveProfileById(id, (profile) => ({
      ...profile,
      kodai_quark_stat: value,
    }));
  }, [updateComprehensiveProfileById]);

  const handleUpdateComprehensiveProfileStatus = useCallback((
    id: string,
    isBurned: boolean,
    isPoisoned: boolean,
    isParalyzed: boolean,
  ) => {
    updateComprehensiveProfileById(id, (profile) => ({
      ...profile,
      isBurned,
      isPoisoned,
      isParalyzed,
    }));
  }, [updateComprehensiveProfileById]);

  const handleRemoveAttackPokemon = (id: string) => {
    // 現在のモードに応じて適切なリストから削除
    if (currentMode === 'attack') {
      setAttackPokemonList(prev => prev.filter(p => p.id !== id));
    } else if (currentMode === 'defense') {
      setDefencePokemonList(prev => prev.filter(p => p.id !== id));
    }

    // If removing the currently selected Pokemon for move selection, clear the selection
    if (selectedAttackPokemonForMove === id) {
      setSelectedAttackPokemonForMove(null);
    }
  };

  const handleCopyAttackPokemon = (id: string) => {
    // 現在のモードに応じて適切なリストからコピー対象を検索
    const currentList = currentMode === 'attack' ? attackPokemonList : defencePokemonList;
    const pokemonToCopy = currentList.find(p => p.id === id);

    if (pokemonToCopy) {
      const copiedPokemon: AttackPokemonData = {
        ...pokemonToCopy,
        id: `${currentMode}-${Date.now()}-${Math.random()}`, // 新しいIDを生成
        // 深いコピーを作成
        statInputs: { ...pokemonToCopy.statInputs },
        pokemon: { ...pokemonToCopy.pokemon }
      };

      // 元のポケモンの位置を探して、その直後に挿入
      const originalIndex = currentList.findIndex(p => p.id === id);

      if (currentMode === 'attack') {
        setAttackPokemonList(prev => {
          const newList = [...prev];
          newList.splice(originalIndex + 1, 0, copiedPokemon);
          return newList;
        });
      } else if (currentMode === 'defense') {
        setDefencePokemonList(prev => {
          const newList = [...prev];
          newList.splice(originalIndex + 1, 0, copiedPokemon);
          return newList;
        });
      }

      showToast('ポケモンをコピーしました', 'success');
    }
  };

  // 攻撃側ステータス更新用のdebounced計算完了通知
  const debouncedShowCalculationComplete = useMemo(
    () => debounce(() => {
      showToast('計算終了', 'success');
    }, 500), // 攻撃側は少し長めの遅延
    [showToast]
  );

  const handleUpdateAttackPokemonStats = (id: string, newStatInputsOrField: AttackStatInputs | string, value?: number) => {
    // 新しい形式: 個別フィールドの更新
    if (typeof newStatInputsOrField === 'string' && value !== undefined) {
      const field = newStatInputsOrField;
      const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
        if (p.id === id) {
          // 既存のstatInputsに個体値フィールドが欠けている場合を補完
          const completeStatInputs = {
            attack_iv: 31,
            defense_iv: 31,
            special_attack_iv: 31,
            special_defense_iv: 31,
            speed_iv: 31,
            ...p.statInputs,
          };
          let nextValue = value;
          if (attackEffortFieldSet.has(field as AttackEffortField)) {
            nextValue = clampAttackEffortWithTotal(
              completeStatInputs as AttackStatInputs,
              field as AttackEffortField,
              value,
            );
          }

          const updatedPokemon = {
            ...p,
            statInputs: {
              ...completeStatInputs,
              [field]: nextValue,
            },
          };

          return updatedPokemon;
        }
        return p;
      });

      if (currentMode === 'attack') {
        setAttackPokemonList(updateFunction);
        // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
        damageCache.clear();
        damageDetailCache.clear();
      } else if (currentMode === 'defense') {
        setDefencePokemonList(updateFunction);
      }
    }
    // 従来の形式: 全体のstatInputsを更新
    else if (typeof newStatInputsOrField === 'object') {
      const normalizedStatInputs = normalizeAttackStatInputs(newStatInputsOrField);
      const updateFunction = (prev: AttackPokemonData[]) => prev.map(p =>
        p.id === id ? { ...p, statInputs: normalizedStatInputs } : p
      );

      if (currentMode === 'attack') {
        setAttackPokemonList(updateFunction);
        // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
        damageCache.clear();
        damageDetailCache.clear();
      } else if (currentMode === 'defense') {
        setDefencePokemonList(updateFunction);
      }
    }

    // 攻撃側のステータス変更時もダメージ計算完了を通知
    debouncedShowCalculationComplete();
  };

  const handleUpdateAttackPokemonPersonality = (id: string, field: string, value: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p =>
      p.id === id ? {
        ...p,
        statInputs: {
          ...p.statInputs,
          [field]: value
        }
      } : p
    );

    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }

    debouncedShowCalculationComplete();
  };

  const handleUpdateAttackPokemonRank = (id: string, field: string, value: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p =>
      p.id === id ? {
        ...p,
        [field]: value
      } : p
    );

    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }

    debouncedShowCalculationComplete();
  };

  const handleSelectMoveForAttackPokemon = (id: string, moveId: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        const selectedMove = getMoveById(moveId);

        // Set rank correction and stat inputs for specific Pokemon + Move combinations
        let rankCorrection: rankCorrectionEnum = p.rankCorrection || 0;
        let statInputs = p.statInputs;
        const pokemonId = p.pokemon.id;

        // ボディプレス: 防御努力値を即時最大にする
        if (isBodyPressMove(selectedMove)) {
          statInputs = {
            ...statInputs,
            defense_effort: MAX_STAT_EFFORT
          };

          // Zamazenta (Hero/Crowned) + Body Press: +1 defense rank
          if (pokemonId === 10189 || pokemonId === 10190) {
            rankCorrection = 1;
          }
        }
        // イカサマ: 攻撃努力値は0からスタート、性格補正は防御側をコピー
        else if (isIkasamaMove(selectedMove) && selectedPokemon) {
          statInputs = {
            ...statInputs,
            attack_effort: 0, // 努力値は0からスタート
            attack_personality: selectedPokemon.personality.attack
          };
        }
        // Zacian (Hero/Crowned): +1 attack rank (preserve existing logic)
        else if ((pokemonId === 10187 || pokemonId === 10188) && rankCorrection === 0) {
          rankCorrection = 1;
        }

        // Tera Blast: Set default tera type to most used if not already set
        let terasuType = p.terasuType;
        if (selectedMove?.id === 851) { // Tera Blast
          if (!terasuType && p.pokemon.often_used_terasu && p.pokemon.often_used_terasu.length > 0) {
            terasuType = p.pokemon.often_used_terasu[0];
          }
        }

        // Critical hit moves: Set default critical hit settings
        let isCritical = p.isCritical;
        let criticalHitCount = p.criticalHitCount;

        if (selectedMove?.id === 817 || // あんこくきょうだ (Wicked Blow)
          selectedMove?.id === 524 || // こおりのいぶき (Frost Breath)
          selectedMove?.id === 870) { // トリックフラワー (Flower Trick)
          isCritical = true;
        } else if (selectedMove?.id === 818) { // すいりゅうれんだ (Surging Strikes)
          isCritical = true;
          criticalHitCount = 3; // デフォルト3回
        }

        // イカサマ技の場合、防御側の攻撃個体値もコピー
        let updatedPokemon = p.pokemon;
        if (isIkasamaMove(selectedMove) && selectedPokemon) {
          updatedPokemon = {
            ...p.pokemon,
            individualValue: {
              ...p.pokemon.individualValue,
              attack: selectedPokemon.individualValue.attack
            }
          };
        }

        const normalizedStatInputs = normalizeAttackStatInputs(statInputs);

        return {
          ...p,
          pokemon: updatedPokemon,
          selectedMoveId: moveId,
          selectedMove,
          rankCorrection,
          statInputs: normalizedStatInputs,
          terasuType,
          isCritical,
          criticalHitCount,
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }

    // 技変更時もダメージ計算完了を通知
    debouncedShowCalculationComplete();
  };

  const handleSelectAbilityForAttackPokemon = (id: string, abilityId: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        // If abilityId is 0, set selectedAbility to undefined to represent "No Ability"
        const selectedAbility = abilityId === 0 ? undefined : ability_list.find(a => a.id === abilityId);
        return { ...p, selectedAbilityId: abilityId, selectedAbility };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }

    // 特性変更時もダメージ計算完了を通知
    debouncedShowCalculationComplete();
  };

  const handleSelectItemForAttackPokemon = (id: string, itemId: OFFENCE_ITEM_ID) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        const fixedItemId = getFixedItemIdForPokemonEntity(p.pokemon);
        if (fixedItemId != null && fixedItemId !== itemId) {
          return p;
        }
        // ゲノセクト以外ではカセット系を選択できないようにする
        if (!canPokemonHoldItem(p.pokemon.id, itemId)) {
          return {
            ...p,
            selectedItemId: OFFENCE_ITEM_ID_NO_SET,
            pokemon: { ...p.pokemon, selected_item_id: OFFENCE_ITEM_ID_NO_SET },
          };
        }
        return {
          ...p,
          selectedItemId: itemId,
          pokemon: { ...p.pokemon, selected_item_id: itemId },
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }

    // アイテム変更時もダメージ計算完了を通知
    debouncedShowCalculationComplete();
  };

  const handleSelectTerasuTypeForAttackPokemon = (id: string, terasuType: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return { ...p, terasuType };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleSelectTerasuStateForAttackPokemon = (id: string, terasuState: 'none' | 'type' | 'stellar') => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return { ...p, terasuState };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleSelectAbilityForDefensePokemon = (abilityId: number) => {
    if (selectedPokemon) {
      // If abilityId is 0, set selected_ability to undefined to represent "No Ability"
      const selectedAbility = abilityId === 0 ? undefined : ability_list.find(a => a.id === abilityId);
      setSelectedPokemon(prev => prev ? {
        ...prev,
        selected_ability_id: abilityId,
        selected_ability: selectedAbility
      } : null);
      // 防御側特性変更時もダメージ計算完了を通知
      setTimeout(() => {
        showToast('計算終了', 'success');
      }, 300);
    }
  };

  const handleUpdateRankCorrection = (id: string, rankCorrection: rankCorrectionEnum) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return { ...p, rankCorrection };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleUpdateAttackPokemonStatus = (
    id: string,
    isBurned: boolean,
    isPoisoned: boolean,
    isParalyzed: boolean,
  ) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          isBurned,
          isPoisoned,
          isParalyzed,
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleUpdateCriticalHit = (id: string, isCritical: boolean, criticalHitCount?: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          isCritical,
          criticalHitCount
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleUpdateCustomPower = (id: string, customPower: number) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          customPower
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleUpdateJuuden = (id: string, isJuuden: boolean) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          isJuuden
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの防御側能力値変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleUpdateDoublePower = (id: string, isDoublePower: boolean) => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          isDoublePower
        };
      }
      return p;
    });

    // 現在のモードに応じて適切なリストを更新
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      // 攻撃モードでの威力変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
      // 防御モードでの威力変更時はキャッシュをクリア
      damageCache.clear();
      damageDetailCache.clear();
    }
  };

  // こだいかっせい/クォークチャージの対象ステータス更新（UI専用化のため分離）
  const handleUpdateKodaiQuarkStat = (id: string, value: 'attack' | 'special_attack') => {
    const updateFunction = (prev: AttackPokemonData[]) => prev.map(p => (
      p.id === id ? { ...p, kodai_quark_stat: value } : p
    ));
    if (currentMode === 'attack') {
      setAttackPokemonList(updateFunction);
      damageCache.clear();
      damageDetailCache.clear();
    } else if (currentMode === 'defense') {
      setDefencePokemonList(updateFunction);
    }
  };

  const handleIndividualValueChange = (statName: "hp" | "attack" | "defense" | "special_attack" | "special_defense" | "speed", value: 0 | 31) => {
    if (selectedPokemon) {
      setSelectedPokemon(prev => prev ? {
        ...prev,
        individualValue: {
          ...prev.individualValue,
          [statName]: value
        }
      } : null);

      // イカサマ技を使用している攻撃側ポケモンの攻撃個体値も同期更新
      if (statName === 'attack') {
        const updateIkasamaFunction = (prev: AttackPokemonData[]) => prev.map(attackPokemon => {
          if (isIkasamaMove(attackPokemon.selectedMove)) {
            return {
              ...attackPokemon,
              pokemon: {
                ...attackPokemon.pokemon,
                individualValue: {
                  ...attackPokemon.pokemon.individualValue,
                  attack: value
                }
              }
            };
          }
          return attackPokemon;
        });

        // 攻撃モードと防御モード両方のリストを更新
        setAttackPokemonList(updateIkasamaFunction);
        setDefencePokemonList(updateIkasamaFunction);
      }
    }
  };

  // 計算完了トーストはデバウンスする（状態更新は即時）
  const debouncedShowCalculationToast = useMemo(
    () => debounce(() => {
      // 初期化中はトーストを抑制
      if (!isInitialized) return;
      setTimeout(() => {
        showToast('計算終了', 'success');
      }, 100); // 少し遅延してUI更新後に表示
    }, 150), // より短い遅延でレスポンシブな操作感を実現
    [showToast, isInitialized],
  );

  // キャッシュキーは計算に影響する入力だけを含める
  const getCacheKey = useCallback((attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting): string => {
    const resolvedOtherSetting = overrideOtherSetting ?? currentOtherSetting;
    const attackData = {
      pokemonId: attackPokemon.pokemon.id,
      moveId: attackPokemon.selectedMove?.id,
      statInputs: attackPokemon.statInputs,
      selectedAbilityId: attackPokemon.selectedAbilityId,
      selectedItemId: attackPokemon.selectedItemId,
      terasuType: attackPokemon.terasuType,
      terasuState: attackPokemon.terasuState,
      isBurned: attackPokemon.isBurned,
      isPoisoned: attackPokemon.isPoisoned,
      isParalyzed: attackPokemon.isParalyzed,
      isJuuden: attackPokemon.isJuuden,
      kodai_quark_stat: attackPokemon.kodai_quark_stat,
      isCritical: attackPokemon.isCritical,
      criticalHitCount: attackPokemon.criticalHitCount,
      isDoublePower: attackPokemon.isDoublePower,
      customPower: attackPokemon.customPower,
      rankCorrection: attackPokemon.rankCorrection
    };

    const defenseData = {
      pokemonId: defensePokemon.id,
      effective_slider_step: defensePokemon.effective_slider_step,
      personality: defensePokemon.personality,
      selected_ability_id: defensePokemon.selected_ability_id,
      selected_item_id: defensePokemon.selected_item_id,
      terasu_type: defensePokemon.terasu_type,
      rankCorrection: defensePokemon.rankCorrection,
      individual: defensePokemon.individualValue,
      other_setting: defensePokemon.other_setting
    };

    return JSON.stringify({ attack: attackData, defense: defenseData, otherSetting: resolvedOtherSetting });
  }, [currentOtherSetting]);

  // Calculate damage for a specific attack Pokemon vs defense Pokemon
  // 攻撃モード専用のダメージ計算関数（テラスタル補正が異なる）
  const calculateDamageAttackMode = useCallback((attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting): string => {
    const resolvedOtherSetting = overrideOtherSetting ?? currentOtherSetting;
    const cacheKey = JSON.stringify({
      attackPokemon: {
        id: attackPokemon.pokemon.id,
        statInputs: attackPokemon.statInputs,
        selectedMoveId: attackPokemon.selectedMoveId,
        selectedAbilityId: attackPokemon.selectedAbilityId,
        selectedItemId: attackPokemon.selectedItemId,
        rankCorrection: attackPokemon.rankCorrection,
        attackRank: attackPokemon.attackRank,
        specialAttackRank: attackPokemon.specialAttackRank,
        terasuType: attackPokemon.terasuType,
        terasuState: attackPokemon.terasuState,
        isBurned: attackPokemon.isBurned,
        isPoisoned: attackPokemon.isPoisoned,
        isParalyzed: attackPokemon.isParalyzed,
        isJuuden: attackPokemon.isJuuden,
        isDoublePower: attackPokemon.isDoublePower,
        customPower: attackPokemon.customPower,
        isCritical: attackPokemon.isCritical,
        criticalHitCount: attackPokemon.criticalHitCount
      },
      defensePokemon: {
        id: defensePokemon.id,
        hp: defensePokemon.hp,
        attack: defensePokemon.attack,
        defense: defensePokemon.defense,
        special_attack: defensePokemon.special_attack,
        special_defense: defensePokemon.special_defense,
        effective_slider_step: defensePokemon.effective_slider_step,
        personality: defensePokemon.personality,
        rankCorrection: defensePokemon.rankCorrection,
        individualValue: defensePokemon.individualValue,
        selected_ability_id: defensePokemon.selected_ability_id,
        selected_item_id: defensePokemon.selected_item_id,
        terasu_type: defensePokemon.terasu_type
      },
      otherSetting: resolvedOtherSetting,
      mode: 'attack' // 攻撃モード専用のキャッシュ
    });

    // キャッシュから結果を取得
    const cachedResult = damageCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    if (!attackPokemon.selectedMove) {
      const result = '技が選択されていません';
      damageCache.set(cacheKey, result);
      return result;
    }

    // Create offense Pokemon interface for calculation
    const offenseInterface = toOffence(attackPokemon.pokemon);

    // Apply attack Pokemon's stat inputs (完全な能力値設定)
    offenseInterface.effective_slider_step.hp = clampEffortPoint(attackPokemon.statInputs.hp_effort);
    offenseInterface.effective_slider_step.attack = clampEffortPoint(attackPokemon.statInputs.attack_effort);
    offenseInterface.effective_slider_step.defense = clampEffortPoint(attackPokemon.statInputs.defense_effort);
    offenseInterface.effective_slider_step.special_attack = clampEffortPoint(attackPokemon.statInputs.special_attack_effort);
    offenseInterface.effective_slider_step.special_defense = clampEffortPoint(attackPokemon.statInputs.special_defense_effort);
    offenseInterface.effective_slider_step.speed = clampEffortPoint(attackPokemon.statInputs.speed_effort);

    // Apply personality corrections (完全な性格補正設定)
    offenseInterface.personality.attack = attackPokemon.statInputs.attack_personality;
    offenseInterface.personality.defense = attackPokemon.statInputs.defense_personality;
    offenseInterface.personality.special_attack = attackPokemon.statInputs.special_attack_personality;
    offenseInterface.personality.special_defense = attackPokemon.statInputs.special_defense_personality;
    offenseInterface.personality.speed = attackPokemon.statInputs.speed_personality;

    // Apply individual values (個体値設定)
    if (attackPokemon.statInputs.attack_iv !== undefined) {
      offenseInterface.individualValue.attack = attackPokemon.statInputs.attack_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.defense_iv !== undefined) {
      offenseInterface.individualValue.defense = attackPokemon.statInputs.defense_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.special_attack_iv !== undefined) {
      offenseInterface.individualValue.special_attack = attackPokemon.statInputs.special_attack_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.special_defense_iv !== undefined) {
      offenseInterface.individualValue.special_defense = attackPokemon.statInputs.special_defense_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.speed_iv !== undefined) {
      offenseInterface.individualValue.speed = attackPokemon.statInputs.speed_iv as individualValueEnum;
    }

    // Set selected move
    offenseInterface.selected_move = attackPokemon.selectedMove;
    offenseInterface.selected_move_id = attackPokemon.selectedMoveId;

    // Set selected ability
    offenseInterface.selected_ability_id = attackPokemon.selectedAbilityId;
    offenseInterface.selected_ability = attackPokemon.selectedAbility;

    // Set selected item
    offenseInterface.selected_item_id = attackPokemon.selectedItemId;

    // Set rank correction（攻撃モードでは攻撃/特攻を分けて扱う）
    if (
      attackPokemon.rankCorrection !== undefined ||
      attackPokemon.attackRank !== undefined ||
      attackPokemon.specialAttackRank !== undefined
    ) {
      const resolvedAttackRank =
        (attackPokemon.attackRank ?? attackPokemon.rankCorrection ?? 0) as rankCorrectionEnum;
      const resolvedSpecialAttackRank =
        (attackPokemon.specialAttackRank ?? attackPokemon.rankCorrection ?? 0) as rankCorrectionEnum;
      offenseInterface.rankCorrection = {
        ...offenseInterface.rankCorrection,
        attack: resolvedAttackRank,
        special_attack: resolvedSpecialAttackRank
      };
    }

    // Set terastal info
    if (attackPokemon.terasuType) {
      offenseInterface.terasu_type = attackPokemon.terasuType as PokemonType;
    }

    // Set other status conditions
    if (attackPokemon.isBurned || attackPokemon.isPoisoned || attackPokemon.isParalyzed || attackPokemon.isJuuden || attackPokemon.isDoublePower || attackPokemon.isCritical || attackPokemon.customPower) {
      const criticalHitCount = attackPokemon.isCritical ? (attackPokemon.criticalHitCount || 1) : 0;
      let nextOtherSetting: calcOtherSetting = {
        ...offenseInterface.other_setting,
        double_power_condition: attackPokemon.isDoublePower || false,
        custom_power: attackPokemon.customPower || 0,
        critical_hit_count: criticalHitCount
      };
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'burned', Boolean(attackPokemon.isBurned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'poisoned', Boolean(attackPokemon.isPoisoned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'paralyzed', Boolean(attackPokemon.isParalyzed));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'juuden', Boolean(attackPokemon.isJuuden));
      offenseInterface.other_setting = nextOtherSetting;
    }

    // Defense Pokemon is already converted with stat adjustments applied
    const defenseWithAbility = defensePokemon;

    // Check for special moves that need item/power replacement
    let moveToUse = attackPokemon.selectedMove;

    // Handle Zacian/Zamazenta Iron Head replacement
    if ((attackPokemon.pokemon.id === 10188 || attackPokemon.pokemon.id === 10189) && attackPokemon.selectedMove.id === 442) {
      moveToUse = { ...attackPokemon.selectedMove, power: 100 };
    }

    if (isRenzokuMove(moveToUse)) {
      // Multi-hit move calculation
      const results: string[] = [];
      const minHits = moveToUse.min_renzoku || 2;
      const maxHits = moveToUse.max_renzoku || 5;

      for (let hitCount = minHits; hitCount <= maxHits; hitCount++) {
        offenseInterface.selected_hit_number = hitCount;

        const result = calc_interface({
          offencePokemonList: [offenseInterface],
          deffencePokemon: defenseWithAbility,
          deffenceDummyPokemon: defenseWithAbility,
          otherSetting: resolvedOtherSetting,
          attackMode: true
        });

        results.push(`${hitCount}発: ${result.text}`);
      }

      const finalResult = results.join('\n');
      const processedResult = finalResult === "" || finalResult.includes("【0～0】") ? "無効" : finalResult;

      damageCache.set(cacheKey, processedResult);
      return processedResult;
    } else {
      // Single-hit move calculation
      const result = calc_interface({
        offencePokemonList: [offenseInterface],
        deffencePokemon: defenseWithAbility,
        deffenceDummyPokemon: defenseWithAbility,
        otherSetting: resolvedOtherSetting,
        attackMode: true
      });

      const processedResult = result.text === "" || result.max_damage === 0 ? "無効" : result.text;

      damageCache.set(cacheKey, processedResult);
      damageDetailCache.set(cacheKey, result);
      return processedResult;
    }
  }, [currentOtherSetting, damageCache, damageDetailCache]);

  const calculateDamage = useCallback((attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting): string => {
    const resolvedOtherSetting = overrideOtherSetting ?? currentOtherSetting;
    if (!attackPokemon.selectedMove) {
      return '技を選択してください';
    }

    // キャッシュチェック
    const cacheKey = getCacheKey(attackPokemon, defensePokemon, resolvedOtherSetting);
    const cachedResult = damageCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    try {
      // Convert to offense interface
      const offenseInterface = toOffence(attackPokemon.pokemon);

      // Special move replacement for Zacian and Zamazenta
      let moveToUse = attackPokemon.selectedMove;
      const pokemonId = attackPokemon.pokemon.id;

      // Replace Iron Head (442) with Behemoth Blade (781) for Zacian (Crowned Sword) only
      if (pokemonId === 10188 && attackPokemon.selectedMove.id === 442) {
        const behemothBlade = getMoveById(781);
        if (behemothBlade) {
          moveToUse = behemothBlade;
        }
      }

      // Replace Iron Head (442) with Behemoth Bash (782) for Zamazenta (Crowned Shield) only
      if (pokemonId === 10189 && attackPokemon.selectedMove.id === 442) {
        const behemothBash = getMoveById(782);
        if (behemothBash) {
          moveToUse = behemothBash;
        }
      }

      // Update stats with current inputs
      offenseInterface.effective_slider_step = {
        ...offenseInterface.effective_slider_step,
        attack: clampEffortPoint(attackPokemon.statInputs.attack_effort),
        special_attack: clampEffortPoint(attackPokemon.statInputs.special_attack_effort),
        defense: clampEffortPoint(attackPokemon.statInputs.defense_effort),
      };

      offenseInterface.personality = {
        ...offenseInterface.personality,
        attack: attackPokemon.statInputs.attack_personality,
        special_attack: attackPokemon.statInputs.special_attack_personality,
        defense: attackPokemon.statInputs.defense_personality
      };

      // Set selected move (use the potentially replaced move)
      offenseInterface.selected_move_id = moveToUse.id;
      offenseInterface.selected_move = moveToUse;

      // Set selected ability
      // NOTE: 「とくせい無し」(0) も含めて、AttackPokemonData 側の選択を常に反映する
      if (attackPokemon.selectedAbilityId !== undefined) {
        offenseInterface.selected_ability_id = attackPokemon.selectedAbilityId;
        offenseInterface.selected_ability = attackPokemon.selectedAbility;
      }

      // Set selected item
      if (attackPokemon.selectedItemId) {
        offenseInterface.selected_item_id = attackPokemon.selectedItemId;
      }

      // Set tera type - for Tera Blast (specific type) or Tera state (Pokemon's original types)
      if (attackPokemon.terasuType) {
        // Tera Blast with specific tera type
        offenseInterface.terasu_type = attackPokemon.terasuType as PokemonType;
      } else if (attackPokemon.terasuState === 'type') {
        // Type Terastal - use Pokemon's first type
        offenseInterface.terasu_type = attackPokemon.pokemon.type_id_list[0] as PokemonType;
      } else if (attackPokemon.terasuState === 'stellar') {
        // Stellar Terastal - use Stellar type (ID 19)
        offenseInterface.terasu_type = 19 as PokemonType;
      }

      // Set Pokemon status (grounded, burned, kodai_quark_stat, critical hit)
      const criticalHitCount = attackPokemon.isCritical ? (attackPokemon.criticalHitCount || 1) : 0;
      let nextOtherSetting: calcOtherSetting = {
        ...offenseInterface.other_setting,
        kodai_quark_stat: attackPokemon.kodai_quark_stat,
        critical_hit_count: criticalHitCount,
        double_power_condition: attackPokemon.isDoublePower
      };
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'burned', Boolean(attackPokemon.isBurned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'poisoned', Boolean(attackPokemon.isPoisoned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'paralyzed', Boolean(attackPokemon.isParalyzed));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'juuden', Boolean(attackPokemon.isJuuden));
      offenseInterface.other_setting = nextOtherSetting;

      // きしかいせい・じたばたのカスタム威力を設定
      if (isKishikaiseiMove(moveToUse) && attackPokemon.customPower) {
        moveToUse = { ...moveToUse, power: attackPokemon.customPower };
        offenseInterface.selected_move = moveToUse;
      }

      // その他のカスタム威力技（ふんどのこぶし、アシストパワー等）の設定
      if ((isHundonokobushiMove(moveToUse) || isStoredPowerMove(moveToUse) || isHakidasuMove(moveToUse)) && attackPokemon.customPower) {
        offenseInterface.other_setting = {
          ...offenseInterface.other_setting,
          custom_power: attackPokemon.customPower
        };
      }

      // おはかまいりのカスタム威力を設定
      if (isOhakamairiMove(moveToUse) && attackPokemon.customPower) {
        offenseInterface.other_setting = {
          ...offenseInterface.other_setting,
          custom_power: attackPokemon.customPower
        };
      }

      // しぼりとる/にぎりつぶすのカスタム威力を設定
      if (isWringOutMove(moveToUse) && attackPokemon.customPower) {
        offenseInterface.other_setting = {
          ...offenseInterface.other_setting,
          custom_power: attackPokemon.customPower
        };
      }

      // Create a copy of defense Pokemon with selected ability (item should already be set)
      const defenseWithAbility = { ...defensePokemon };
      if (defensePokemon.selected_ability_id && defensePokemon.selected_ability) {
        defenseWithAbility.selected_ability_id = defensePokemon.selected_ability_id;
        defenseWithAbility.selected_ability = defensePokemon.selected_ability;
      }

      // イカサマ技の場合、攻撃の個体値も防御側から取得
      if (isIkasamaMove(moveToUse)) {
        offenseInterface.individualValue.attack = defensePokemon.individualValue.attack;
      }

      // Apply rank correction based on move type
      // NOTE: ボディプレスは防御値で攻撃するため、防御ランク補正を適用する
      if (attackPokemon.rankCorrection !== undefined) {
        if (isBodyPressMove(moveToUse)) {
          offenseInterface.rankCorrection.defense = attackPokemon.rankCorrection;
        } else if (isPhysicalMove(moveToUse)) {
          // Physical move - apply attack rank correction
          offenseInterface.rankCorrection.attack = attackPokemon.rankCorrection;
        } else if (isSpecialMove(moveToUse)) {
          // Special move - apply special attack rank correction
          offenseInterface.rankCorrection.special_attack = attackPokemon.rankCorrection;
        }
      }

      // Check if this is a multi-hit move (use the potentially replaced move)
      if (isRenzokuMove(moveToUse)) {
        // Calculate damage for each possible hit count using move's min/max values
        const results: string[] = [];
        const minHits = moveToUse.min_renzoku || 2;
        const maxHits = moveToUse.max_renzoku || 5;

        for (let hitCount = minHits; hitCount <= maxHits; hitCount++) {
          // Set the hit number for this calculation
          offenseInterface.selected_hit_number = hitCount;

          const result = calc_interface({
            offencePokemonList: [offenseInterface],
            deffencePokemon: defenseWithAbility,
            deffenceDummyPokemon: defenseWithAbility,
            otherSetting: resolvedOtherSetting,
            attackMode: false
          });

          results.push(`${hitCount}発: ${result.text}`);
        }

        const finalResult = results.join('\n');
        // 最初のヒットの結果も保存（モーダル表示用）
        if (results.length > 0) {
          // 最初のヒット数で再計算してretCalcオブジェクトを取得
          offenseInterface.selected_hit_number = minHits;
          const firstHitResult = calc_interface({
            offencePokemonList: [offenseInterface],
            deffencePokemon: defenseWithAbility,
            deffenceDummyPokemon: defenseWithAbility,
            otherSetting: resolvedOtherSetting,
            attackMode: false
          });
          damageDetailCache.set(cacheKey, firstHitResult);
        }

        // ダメージが0の場合（タイプ相性で無効など）は「無効」として表示
        const processedResult = finalResult === "" || finalResult.includes("【0～0】") ? "無効" : finalResult;

        // キャッシュに保存
        damageCache.set(cacheKey, processedResult);
        return processedResult;
      } else {
        // Single-hit move calculation
        const result = calc_interface({
          offencePokemonList: [offenseInterface],
          deffencePokemon: defenseWithAbility,
          deffenceDummyPokemon: defenseWithAbility,
          otherSetting: resolvedOtherSetting,
          attackMode: false
        });

        // ダメージが0の場合（タイプ相性で無効など）は「無効」として表示
        const finalText = result.text === "" || result.max_damage === 0 ? "無効" : result.text;

        // キャッシュに保存
        damageCache.set(cacheKey, finalText);
        damageDetailCache.set(cacheKey, result);
        return finalText;
      }
    } catch (error) {
      const errorResult = 'ダメージ計算エラー';
      // エラーの場合もキャッシュして無限ループを防ぐ
      damageCache.set(cacheKey, errorResult);
      return errorResult;
    }
  }, [getCacheKey, damageCache, damageDetailCache, currentOtherSetting]);

  // デバウンス処理付きのダメージ計算関数
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const debouncedCalculateDamage = useMemo(
    () => debounce(calculateDamage, 300),
    [calculateDamage]
  );

  // ダメージ計算の詳細情報を取得する関数（キャッシュから）
  // 攻撃モード専用のダメージ詳細取得関数（テラスタル補正が異なる）
  const getDamageDetailAttackMode = useCallback((attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting): retCalc => {
    const resolvedOtherSetting = overrideOtherSetting ?? currentOtherSetting;
    const cacheKey = JSON.stringify({
      attackPokemon: {
        id: attackPokemon.pokemon.id,
        statInputs: attackPokemon.statInputs,
        selectedMoveId: attackPokemon.selectedMoveId,
        selectedAbilityId: attackPokemon.selectedAbilityId,
        selectedItemId: attackPokemon.selectedItemId,
        rankCorrection: attackPokemon.rankCorrection,
        attackRank: attackPokemon.attackRank,
        specialAttackRank: attackPokemon.specialAttackRank,
        terasuType: attackPokemon.terasuType,
        terasuState: attackPokemon.terasuState,
        isBurned: attackPokemon.isBurned,
        isPoisoned: attackPokemon.isPoisoned,
        isJuuden: attackPokemon.isJuuden,
        isDoublePower: attackPokemon.isDoublePower,
        customPower: attackPokemon.customPower,
        isCritical: attackPokemon.isCritical,
        criticalHitCount: attackPokemon.criticalHitCount,
      },
      defensePokemon: {
        id: defensePokemon.id,
        hp: defensePokemon.hp,
        defense: defensePokemon.defense,
        special_defense: defensePokemon.special_defense,
        effective_slider_step: defensePokemon.effective_slider_step,
        personality: defensePokemon.personality,
        rankCorrection: defensePokemon.rankCorrection,
        individualValue: defensePokemon.individualValue,
        selected_ability_id: defensePokemon.selected_ability_id,
        selected_item_id: defensePokemon.selected_item_id,
        terasu_type: defensePokemon.terasu_type
      },
      otherSetting: resolvedOtherSetting,
      mode: 'attack_detail' // 攻撃モード詳細専用のキャッシュ
    });

    const cachedResult = damageDetailCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    if (!attackPokemon.selectedMove) {
      const result = { text: '技が選択されていません', min_damage: 0, max_damage: 0, random_table: {} };
      damageDetailCache.set(cacheKey, result);
      return result;
    }

    // Create offense Pokemon interface for calculation
    const offenseInterface = toOffence(attackPokemon.pokemon);

    // Apply all the same settings as calculateDamageAttackMode (完全な能力値設定)
    offenseInterface.effective_slider_step.hp = clampEffortPoint(attackPokemon.statInputs.hp_effort);
    offenseInterface.effective_slider_step.attack = clampEffortPoint(attackPokemon.statInputs.attack_effort);
    offenseInterface.effective_slider_step.defense = clampEffortPoint(attackPokemon.statInputs.defense_effort);
    offenseInterface.effective_slider_step.special_attack = clampEffortPoint(attackPokemon.statInputs.special_attack_effort);
    offenseInterface.effective_slider_step.special_defense = clampEffortPoint(attackPokemon.statInputs.special_defense_effort);
    offenseInterface.effective_slider_step.speed = clampEffortPoint(attackPokemon.statInputs.speed_effort);

    offenseInterface.personality.attack = attackPokemon.statInputs.attack_personality;
    offenseInterface.personality.defense = attackPokemon.statInputs.defense_personality;
    offenseInterface.personality.special_attack = attackPokemon.statInputs.special_attack_personality;
    offenseInterface.personality.special_defense = attackPokemon.statInputs.special_defense_personality;
    offenseInterface.personality.speed = attackPokemon.statInputs.speed_personality;

    // Apply individual values (個体値設定)

    // console.log(
    //   attackPokemon.statInputs.attack_iv,
    //   attackPokemon.statInputs.defense_iv,
    //   attackPokemon.statInputs.special_defense_iv,
    //   attackPokemon.statInputs.speed_iv,
    // )
    if (attackPokemon.statInputs.attack_iv !== undefined) {
      offenseInterface.individualValue.attack = attackPokemon.statInputs.attack_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.defense_iv !== undefined) {
      offenseInterface.individualValue.defense = attackPokemon.statInputs.defense_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.special_attack_iv !== undefined) {
      offenseInterface.individualValue.special_attack = attackPokemon.statInputs.special_attack_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.special_defense_iv !== undefined) {
      offenseInterface.individualValue.special_defense = attackPokemon.statInputs.special_defense_iv as individualValueEnum;
    }
    if (attackPokemon.statInputs.speed_iv !== undefined) {
      offenseInterface.individualValue.speed = attackPokemon.statInputs.speed_iv as individualValueEnum;
    }

    offenseInterface.selected_move = attackPokemon.selectedMove;
    offenseInterface.selected_move_id = attackPokemon.selectedMoveId;

    offenseInterface.selected_ability_id = attackPokemon.selectedAbilityId;
    offenseInterface.selected_ability = attackPokemon.selectedAbility;

    offenseInterface.selected_item_id = attackPokemon.selectedItemId;

    if (
      attackPokemon.rankCorrection !== undefined ||
      attackPokemon.attackRank !== undefined ||
      attackPokemon.specialAttackRank !== undefined
    ) {
      const resolvedAttackRank =
        (attackPokemon.attackRank ?? attackPokemon.rankCorrection ?? 0) as rankCorrectionEnum;
      const resolvedSpecialAttackRank =
        (attackPokemon.specialAttackRank ?? attackPokemon.rankCorrection ?? 0) as rankCorrectionEnum;
      offenseInterface.rankCorrection = {
        ...offenseInterface.rankCorrection,
        attack: resolvedAttackRank,
        special_attack: resolvedSpecialAttackRank
      };
    }

    if (attackPokemon.terasuType) {
      offenseInterface.terasu_type = attackPokemon.terasuType as PokemonType;
    }

    if (attackPokemon.isBurned || attackPokemon.isPoisoned || attackPokemon.isParalyzed || attackPokemon.isJuuden || attackPokemon.isDoublePower || attackPokemon.isCritical || attackPokemon.customPower) {
      const criticalHitCount = attackPokemon.isCritical ? (attackPokemon.criticalHitCount || 1) : 0;
      let nextOtherSetting: calcOtherSetting = {
        ...offenseInterface.other_setting,
        double_power_condition: attackPokemon.isDoublePower || false,
        custom_power: attackPokemon.customPower || 0,
        critical_hit_count: criticalHitCount
      };
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'burned', Boolean(attackPokemon.isBurned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'poisoned', Boolean(attackPokemon.isPoisoned));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'paralyzed', Boolean(attackPokemon.isParalyzed));
      nextOtherSetting = setPokemonStatus(nextOtherSetting, 'juuden', Boolean(attackPokemon.isJuuden));
      offenseInterface.other_setting = nextOtherSetting;
    }

    // Defense Pokemon is already converted with stat adjustments applied
    const defenseWithAbility = defensePokemon;

    // Handle special move replacements
    let moveToUse = attackPokemon.selectedMove;
    if ((attackPokemon.pokemon.id === 10188 || attackPokemon.pokemon.id === 10189) && attackPokemon.selectedMove.id === 442) {
      moveToUse = { ...attackPokemon.selectedMove, power: 100 };
    }

    if (isRenzokuMove(moveToUse)) {
      const minHits = moveToUse.min_renzoku || 2;
      offenseInterface.selected_hit_number = minHits;
    }

    // console.log(offenseInterface)

    const result = calc_interface({
      offencePokemonList: [offenseInterface],
      deffencePokemon: defenseWithAbility,
      deffenceDummyPokemon: defenseWithAbility,
      otherSetting: resolvedOtherSetting,
      attackMode: true
    });
    // console.log(result)
    damageDetailCache.set(cacheKey, result);
    return result;
  }, [currentOtherSetting, damageDetailCache]);

  const getDamageDetail = useCallback((attackPokemon: AttackPokemonData, defensePokemon: PokemonV2, overrideOtherSetting?: otherSetting): retCalc => {
    const cacheKey = getCacheKey(attackPokemon, defensePokemon, overrideOtherSetting);
    const cachedResult = damageDetailCache.get(cacheKey);

    if (cachedResult) {
      return cachedResult;
    }

    // キャッシュにない場合は空のretCalcを返す
    return { text: 'ダメージ計算エラー', min_damage: 0, max_damage: 0, random_table: {} };
  }, [getCacheKey, damageDetailCache]);

  // 攻撃モード用：左側ポケモンの全技 → 右側ポケモンへのダメージ計算
  const calculateAttackModeDamage = useCallback((
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ): string[] => {
    if (!attackerPokemon.selected_moves || attackerPokemon.selected_moves.length === 0) {
      return ['技が選択されていません'];
    }

    // 変化技を除外した攻撃技のみを対象とする
    const attackMoves = attackerPokemon.selected_moves.filter(isDamagingMove);

    if (attackMoves.length === 0) {
      return ['攻撃技が選択されていません'];
    }

    const results: string[] = [];

    attackMoves.forEach(move => {
      const moveSetting = resolveMoveSetting?.(move.id);
      const resolvedJuuden = moveSetting?.isJuuden ?? isJuudenStatus(attackerPokemon.other_setting);

      // 攻撃側ポケモンをAttackPokemonData形式に変換
      const attackPokemonData: AttackPokemonData = {
        pokemon: attackerPokemon,
        statInputs: {
          hp_effort: attackerPokemon.effective_slider_step.hp,
          attack_effort: attackerPokemon.effective_slider_step.attack,
          defense_effort: attackerPokemon.effective_slider_step.defense,
          special_attack_effort: attackerPokemon.effective_slider_step.special_attack,
          special_defense_effort: attackerPokemon.effective_slider_step.special_defense,
          speed_effort: attackerPokemon.effective_slider_step.speed,
          attack_personality: attackerPokemon.personality.attack,
          defense_personality: attackerPokemon.personality.defense,
          special_attack_personality: attackerPokemon.personality.special_attack,
          special_defense_personality: attackerPokemon.personality.special_defense,
          speed_personality: attackerPokemon.personality.speed,
          // 個体値を左側ポケモンから取得
          attack_iv: attackerPokemon.individualValue?.attack!==undefined?attackerPokemon.individualValue?.attack: 31,
          special_attack_iv: attackerPokemon.individualValue?.special_attack!==undefined?attackerPokemon.individualValue?.special_attack: 31,
          defense_iv: attackerPokemon.individualValue?.defense!==undefined?attackerPokemon.individualValue?.defense: 31,
          special_defense_iv: attackerPokemon.individualValue?.special_defense!==undefined?attackerPokemon.individualValue?.special_defense: 31,
          speed_iv: attackerPokemon.individualValue?.speed!==undefined?attackerPokemon.individualValue?.speed: 31
        },
        id: `attack-mode-${attackerPokemon.id}-${move.id}`,
        selectedMoveId: move.id,
        selectedMove: move,
        selectedAbilityId: attackerPokemon.selected_ability_id,
        selectedAbility: attackerPokemon.selected_ability,
        selectedItemId: attackerPokemon.selected_item_id,
        rankCorrection: attackerPokemon.rankCorrection?.attack || 0,
        attackRank: attackerPokemon.rankCorrection?.attack ?? 0,
        specialAttackRank: attackerPokemon.rankCorrection?.special_attack ?? 0,
        terasuType: attackerPokemon.terasu_type,
        terasuState: attackerPokemon.terasu_type ? 'type' : 'none',
        isBurned: hasPokemonStatus(attackerPokemon.other_setting, 'burned'),
        isPoisoned: hasPokemonStatus(attackerPokemon.other_setting, 'poisoned'),
        isParalyzed: hasPokemonStatus(attackerPokemon.other_setting, 'paralyzed'),
        isJuuden: resolvedJuuden,
        isDoublePower: moveSetting?.isDoublePower,
        customPower: moveSetting?.customPower,
        isCritical: moveSetting?.isCritical,
        criticalHitCount: moveSetting?.criticalHitCount,
      };

      // 右側ポケモンをdefensePoemon形式に変換（新しいステータスを反映）
      // 重要: イカサマ以外の技では防御側の攻撃関連能力値をデフォルト値で固定
      const defensePokemon: PokemonV2 = {
        ...targetPokemon.pokemon,
        effective_slider_step: {
          ...targetPokemon.pokemon.effective_slider_step,
          hp: clampEffortPoint(targetPokemon.statInputs.hp_effort || 0),
          // イカサマのみ防御側の攻撃努力値を使用、他は0
          attack: isIkasamaMove(move) ? clampEffortPoint(targetPokemon.statInputs.attack_effort || 0) : 0,
          defense: clampEffortPoint(targetPokemon.statInputs.defense_effort || 0),
          special_attack: clampEffortPoint(targetPokemon.statInputs.special_attack_effort || 0),
          special_defense: clampEffortPoint(targetPokemon.statInputs.special_defense_effort || 0),
          speed: clampEffortPoint(targetPokemon.statInputs.speed_effort || 0),
        },
        personality: {
          ...targetPokemon.pokemon.personality,
          // イカサマのみ防御側の攻撃性格補正を使用、他は1.0
          attack: isIkasamaMove(move) ? (targetPokemon.statInputs.attack_personality || 1.0) : 1.0,
          defense: targetPokemon.statInputs.defense_personality || 1.0,
          special_attack: targetPokemon.statInputs.special_attack_personality || 1.0,
          special_defense: targetPokemon.statInputs.special_defense_personality || 1.0,
          speed: targetPokemon.statInputs.speed_personality || 1.0
        },
        rankCorrection: {
          ...targetPokemon.pokemon.rankCorrection,
          // イカサマのみ防御側の攻撃ランク補正を使用、他は0
          attack: isIkasamaMove(move) ? ((targetPokemon.attackRank || 0) as rankCorrectionEnum) : 0,
          defense: (targetPokemon.defenseRank || 0) as rankCorrectionEnum,
          special_attack: (targetPokemon.specialAttackRank || 0) as rankCorrectionEnum,
          special_defense: (targetPokemon.specialDefenseRank || 0) as rankCorrectionEnum,
          speed: (targetPokemon.speedRank || 0) as rankCorrectionEnum
        },
        individualValue: {
          ...targetPokemon.pokemon.individualValue,
          // イカサマのみ防御側の攻撃個体値を使用、他は31（デフォルト）
          attack: isIkasamaMove(move) ? (targetPokemon.statInputs?.attack_iv!==undefined?targetPokemon.statInputs?.attack_iv: 31) as individualValueEnum : 31,
          defense: (targetPokemon.statInputs?.defense_iv!==undefined?targetPokemon.statInputs?.defense_iv: 31) as individualValueEnum,
          special_attack: (targetPokemon.statInputs?.special_attack_iv!==undefined?targetPokemon.statInputs?.special_attack_iv: 31) as individualValueEnum,
          special_defense: (targetPokemon.statInputs?.special_defense_iv!==undefined?targetPokemon.statInputs?.special_defense_iv: 31) as individualValueEnum,
          speed: (targetPokemon.statInputs?.speed_iv!==undefined?targetPokemon.statInputs?.speed_iv: 31) as individualValueEnum
        },
        terasu_type: targetPokemon.terasuType as PokemonType | undefined,
        selected_ability_id: targetPokemon.selectedAbilityId,
        selected_ability: targetPokemon.selectedAbility,
        selected_item_id: targetPokemon.selectedItemId
      };
      defensePokemon.other_setting = setPokemonStatus(
        setPokemonStatus(
          setPokemonStatus(defensePokemon.other_setting, 'burned', Boolean(targetPokemon.isBurned)),
          'poisoned',
          Boolean(targetPokemon.isPoisoned),
        ),
        'paralyzed',
        Boolean(targetPokemon.isParalyzed),
      );

      // 攻撃モード専用のダメージ計算関数を使用
      const damageResult = calculateDamageAttackMode(attackPokemonData, defensePokemon, overrideOtherSetting);
      results.push(damageResult);
    });

    return results;
  }, [calculateDamageAttackMode]);

  // 攻撃モード用：左側ポケモンの全技 → 右側ポケモンへのダメージ詳細を取得
  const getAttackModeDamageDetail = useCallback((
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ): retCalc[] => {
    if (!attackerPokemon.selected_moves || attackerPokemon.selected_moves.length === 0) {
      return [{ text: '技が選択されていません', min_damage: 0, max_damage: 0, random_table: {} }];
    }

    // 変化技を除外した攻撃技のみを対象とする
    const attackMoves = attackerPokemon.selected_moves.filter(isDamagingMove);

    if (attackMoves.length === 0) {
      return [{ text: '攻撃技が選択されていません', min_damage: 0, max_damage: 0, random_table: {} }];
    }

    const results: retCalc[] = [];

    // console.log(attackerPokemon.individualValue)

    attackMoves.forEach(move => {
      const moveSetting = resolveMoveSetting?.(move.id);
      const resolvedJuuden = moveSetting?.isJuuden ?? isJuudenStatus(attackerPokemon.other_setting);

      // 攻撃側ポケモンをAttackPokemonData形式に変換
      const attackPokemonData: AttackPokemonData = {
        pokemon: attackerPokemon,
        statInputs: {
          hp_effort: attackerPokemon.effective_slider_step.hp,
          attack_effort: attackerPokemon.effective_slider_step.attack,
          defense_effort: attackerPokemon.effective_slider_step.defense,
          special_attack_effort: attackerPokemon.effective_slider_step.special_attack,
          special_defense_effort: attackerPokemon.effective_slider_step.special_defense,
          speed_effort: attackerPokemon.effective_slider_step.speed,
          attack_personality: attackerPokemon.personality.attack,
          defense_personality: attackerPokemon.personality.defense,
          special_attack_personality: attackerPokemon.personality.special_attack,
          special_defense_personality: attackerPokemon.personality.special_defense,
          speed_personality: attackerPokemon.personality.speed,
          // 個体値を左側ポケモンから取得
          attack_iv: attackerPokemon.individualValue?.attack !== undefined ? attackerPokemon.individualValue?.attack : 31,
          special_attack_iv: attackerPokemon.individualValue?.special_attack !== undefined ? attackerPokemon.individualValue?.special_attack : 31,
          defense_iv: attackerPokemon.individualValue?.defense !== undefined ? attackerPokemon.individualValue?.defense : 31,
          special_defense_iv: attackerPokemon.individualValue?.special_defense !== undefined ? attackerPokemon.individualValue?.special_defense : 31,
          speed_iv: attackerPokemon.individualValue?.speed !== undefined ? attackerPokemon.individualValue?.speed : 31
        },
        id: `attack-mode-${attackerPokemon.id}-${move.id}`,
        selectedMoveId: move.id,
        selectedMove: move,
        selectedAbilityId: attackerPokemon.selected_ability_id,
        selectedAbility: attackerPokemon.selected_ability,
        selectedItemId: attackerPokemon.selected_item_id,
        rankCorrection: attackerPokemon.rankCorrection?.attack || 0,
        attackRank: attackerPokemon.rankCorrection?.attack ?? 0,
        specialAttackRank: attackerPokemon.rankCorrection?.special_attack ?? 0,
        terasuType: attackerPokemon.terasu_type,
        terasuState: attackerPokemon.terasu_type ? 'type' : 'none',
        isBurned: hasPokemonStatus(attackerPokemon.other_setting, 'burned'),
        isPoisoned: hasPokemonStatus(attackerPokemon.other_setting, 'poisoned'),
        isParalyzed: hasPokemonStatus(attackerPokemon.other_setting, 'paralyzed'),
        isJuuden: resolvedJuuden,
        isDoublePower: moveSetting?.isDoublePower,
        customPower: moveSetting?.customPower,
        isCritical: moveSetting?.isCritical,
        criticalHitCount: moveSetting?.criticalHitCount,
      };

      // 右側ポケモンをdefensePoemon形式に変換（新しいステータスを反映）
      // 重要: イカサマ以外の技では防御側の攻撃関連能力値をデフォルト値で固定
      const defensePokemon: PokemonV2 = {
        ...targetPokemon.pokemon,
        effective_slider_step: {
          ...targetPokemon.pokemon.effective_slider_step,
          hp: clampEffortPoint(targetPokemon.statInputs.hp_effort || 0),
          // イカサマのみ防御側の攻撃努力値を使用、他は0
          attack: isIkasamaMove(move) ? clampEffortPoint(targetPokemon.statInputs.attack_effort || 0) : 0,
          defense: clampEffortPoint(targetPokemon.statInputs.defense_effort || 0),
          special_attack: clampEffortPoint(targetPokemon.statInputs.special_attack_effort || 0),
          special_defense: clampEffortPoint(targetPokemon.statInputs.special_defense_effort || 0),
          speed: clampEffortPoint(targetPokemon.statInputs.speed_effort || 0),
        },
        personality: {
          ...targetPokemon.pokemon.personality,
          // イカサマのみ防御側の攻撃性格補正を使用、他は1.0
          attack: isIkasamaMove(move) ? (targetPokemon.statInputs.attack_personality || 1.0) : 1.0,
          defense: targetPokemon.statInputs.defense_personality || 1.0,
          special_attack: targetPokemon.statInputs.special_attack_personality || 1.0,
          special_defense: targetPokemon.statInputs.special_defense_personality || 1.0,
          speed: targetPokemon.statInputs.speed_personality || 1.0
        },
        rankCorrection: {
          ...targetPokemon.pokemon.rankCorrection,
          // イカサマのみ防御側の攻撃ランク補正を使用、他は0
          attack: isIkasamaMove(move) ? ((targetPokemon.attackRank || 0) as rankCorrectionEnum) : 0,
          defense: (targetPokemon.defenseRank || 0) as rankCorrectionEnum,
          special_attack: (targetPokemon.specialAttackRank || 0) as rankCorrectionEnum,
          special_defense: (targetPokemon.specialDefenseRank || 0) as rankCorrectionEnum,
          speed: (targetPokemon.speedRank || 0) as rankCorrectionEnum
        },
        individualValue: {
          ...targetPokemon.pokemon.individualValue,
          // イカサマのみ防御側の攻撃個体値を使用、他は31（デフォルト）
          attack: isIkasamaMove(move) ? (targetPokemon.statInputs?.attack_iv!==undefined?targetPokemon.statInputs?.attack_iv: 31) as individualValueEnum : 31,
          defense: (targetPokemon.statInputs?.defense_iv!==undefined?targetPokemon.statInputs?.defense_iv: 31) as individualValueEnum,
          special_attack: (targetPokemon.statInputs?.special_attack_iv!==undefined?targetPokemon.statInputs?.special_attack_iv: 31) as individualValueEnum,
          special_defense: (targetPokemon.statInputs?.special_defense_iv!==undefined?targetPokemon.statInputs?.special_defense_iv: 31) as individualValueEnum,
          speed: (targetPokemon.statInputs?.speed_iv!==undefined?targetPokemon.statInputs?.speed_iv: 31) as individualValueEnum
        },
        terasu_type: targetPokemon.terasuType as PokemonType | undefined,
        selected_ability_id: targetPokemon.selectedAbilityId,
        selected_ability: targetPokemon.selectedAbility,
        selected_item_id: targetPokemon.selectedItemId
      };
      defensePokemon.other_setting = setPokemonStatus(
        setPokemonStatus(
          setPokemonStatus(defensePokemon.other_setting, 'burned', Boolean(targetPokemon.isBurned)),
          'poisoned',
          Boolean(targetPokemon.isPoisoned),
        ),
        'paralyzed',
        Boolean(targetPokemon.isParalyzed),
      );

      // 攻撃モード専用のダメージ詳細取得関数を使用
      const damageDetail = getDamageDetailAttackMode(attackPokemonData, defensePokemon, overrideOtherSetting);
      results.push(damageDetail);
    });

    return results;
  }, [getDamageDetailAttackMode]);

  // Get available defense items (未使用のためコメントアウト)
  // Get all available items (攻撃側・防御側の道具を統合)
  const getAllItems = useMemo<ItemOption[]>(() => {
    const itemMap = new Map<number, ItemOption>();
    POKEMON_ITEMS.forEach((item) => {
      itemMap.set(item.id, { id: item.id, name: item.name });
    });

    const seen = new Set<number>();
    const ordered = ITEM_ORDER.flatMap((itemId) => {
      const option = itemMap.get(itemId);
      if (!option) return [];
      seen.add(itemId);
      return [option];
    });

    const remaining = POKEMON_ITEMS
      .filter((item) => !seen.has(item.id))
      .map((item) => ({ id: item.id, name: item.name }));

    return [...ordered, ...remaining];
  }, []);

  // Get display name for move (with special replacements for Zacian/Zamazenta)
  const getMoveDisplayName = (move: Move, pokemonId: number): string => {
    // Replace Iron Head display for Zacian (Crowned Sword)
    if (pokemonId === 10188 && move.id === 442) {
      return 'きょじゅうざん';
    }

    // Replace Iron Head display for Zamazenta (Crowned Shield)
    if (pokemonId === 10189 && move.id === 442) {
      return 'きょじゅうだん';
    }

    return move.name_ja;
  };

  // Get move power for display (with special replacements for Zacian/Zamazenta)
  const getMoveDisplayPower = (move: Move, pokemonId: number): number | string => {
    // Replace Iron Head power for Zacian (Crowned Sword) - Behemoth Blade power: 100
    if (pokemonId === 10188 && move.id === 442) {
      return 100;
    }

    // Replace Iron Head power for Zamazenta (Crowned Shield) - Behemoth Bash power: 100
    if (pokemonId === 10189 && move.id === 442) {
      return 100;
    }

    return move.power || '-';
  };

  // Get the appropriate rank type for a move (attack for physical, special attack for special)
  const getRankTypeForMove = (move: Move): string => {
    if (isBodyPressMove(move)) {
      return '防御ランク';
    } else if (isPhysicalMove(move)) {
      return '攻撃ランク';
    } else if (isSpecialMove(move)) {
      return '特攻ランク';
    }
    return '威力ランク';
  };



  const totalEvs = useMemo(() => sumEfforts(statInputs), [statInputs]);

  const selectedPokemonForComprehensive = useMemo(() => {
    if (!selectedPokemon) {
      return null;
    }
    return {
      ...selectedPokemon,
      effective_slider_step: {
        ...selectedPokemon.effective_slider_step,
        hp: clampEffortPoint(statInputs.hp_effort),
        attack: clampEffortPoint(statInputs.attack_effort),
        defense: clampEffortPoint(statInputs.defense_effort),
        special_attack: clampEffortPoint(statInputs.special_attack_effort),
        special_defense: clampEffortPoint(statInputs.special_defense_effort),
        speed: clampEffortPoint(statInputs.speed_effort),
      },
      personality: {
        ...selectedPokemon.personality,
        attack: statInputs.attack_personality,
        defense: statInputs.defense_personality,
        special_attack: statInputs.special_attack_personality,
        special_defense: statInputs.special_defense_personality,
        speed: statInputs.speed_personality,
      },
    };
  }, [selectedPokemon, statInputs]);


  const handleStatChange = (
    statName: EffortField,
    value: number,
  ) => {
    const adjustedValue = clampEffortWithTotal(statInputs, statName, value);
    const finalStats = { ...statInputs, [statName]: adjustedValue };
    setStatInputs(finalStats);

    if (selectedPokemon) {
      const tempPokemon = {
        ...selectedPokemon,
        effective_slider_step: {
          ...selectedPokemon.effective_slider_step,
          [effortFieldMap[statName]]: adjustedValue,
        },
      };
      setSelectedPokemon(tempPokemon);
      debouncedShowCalculationToast();
    }
  };

  const handlePersonalityChange = (statName: 'attack' | 'defense' | 'special_attack' | 'special_defense' | 'speed', value: number) => {
    setStatInputs(prev => ({ ...prev, [`${statName}_personality`]: value }));

    if (selectedPokemon) {
      const tempPokemon = { ...selectedPokemon };
      if (statName === 'attack') {
        tempPokemon.personality.attack = value as PersonalityRate;
      } else if (statName === 'defense') {
        tempPokemon.personality.defense = value as PersonalityRate;
      } else if (statName === 'special_attack') {
        tempPokemon.personality.special_attack = value as PersonalityRate;
      } else if (statName === 'special_defense') {
        tempPokemon.personality.special_defense = value as PersonalityRate;
      } else if (statName === 'speed') {
        tempPokemon.personality.speed = value as PersonalityRate;
      }
      setSelectedPokemon(tempPokemon);

      // イカサマ技を使用している攻撃側ポケモンの性格補正も同期更新
      if (statName === 'attack') {
        const updateIkasamaPersonalityFunction = (prev: AttackPokemonData[]) => prev.map(attackPokemon => {
          if (isIkasamaMove(attackPokemon.selectedMove)) {
            return {
              ...attackPokemon,
              statInputs: {
                ...attackPokemon.statInputs,
                attack_personality: value as PersonalityRate
              }
            };
          }
          return attackPokemon;
        });

        // 攻撃モードと防御モード両方のリストを更新
        setAttackPokemonList(updateIkasamaPersonalityFunction);
        setDefencePokemonList(updateIkasamaPersonalityFunction);
      }
    }
  };

  const saveLeftSideToStorage = useCallback((shouldNotify: boolean) => {
    try {
      const dataToSave = {
        selectedPokemon,
        statInputs,
        selectedDefenseItem,
        otherSetting: currentOtherSetting,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('pokemonSelectPageData', JSON.stringify(dataToSave));
      if (shouldNotify) {
        showToast('データを保存しました', 'success');
      }
    } catch (error) {
      if (shouldNotify) {
        showToast('保存に失敗しました', 'error');
      }
    }
  }, [selectedPokemon, statInputs, selectedDefenseItem, currentOtherSetting, showToast]);

  // Save current state to localStorage (左側のポケモン情報のみ)
  const handleSaveToStorage = useCallback(() => {
    saveLeftSideToStorage(true);
  }, [saveLeftSideToStorage]);

  // Load state from localStorage (左側のポケモン情報のみ)
  const handleLoadFromStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem('pokemonSelectPageData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        const loadedPokemon = parsedData.selectedPokemon as PokemonV2 | null | undefined;
        if (loadedPokemon && !canRestorePokemon(loadedPokemon)) {
          showToast('保存データに不正なポケモンが含まれているため読み込めません', 'error');
          return;
        }
        if (loadedPokemon) {
          const candidateItemId = loadedPokemon.selected_item_id as DEFENCE_ITEM_ID | undefined;
          const normalizedItemId =
            candidateItemId != null && !canPokemonHoldItem(loadedPokemon.id, candidateItemId)
              ? DEFENCE_ITEM_ID_NO_SET
              : candidateItemId;
          setSelectedPokemon(
            normalizedItemId !== candidateItemId
              ? { ...loadedPokemon, selected_item_id: normalizedItemId }
              : loadedPokemon,
          );
        }
        if (parsedData.statInputs) {
          setStatInputs(normalizeEffortInputs(parsedData.statInputs));
        }
        if (parsedData.selectedDefenseItem) {
          const candidateItemId = parsedData.selectedDefenseItem as DEFENCE_ITEM_ID;
          const normalizedItemId =
            loadedPokemon && !canPokemonHoldItem(loadedPokemon.id, candidateItemId)
              ? DEFENCE_ITEM_ID_NO_SET
              : candidateItemId;
          setSelectedDefenseItem(normalizedItemId);
        }
        if (parsedData.otherSetting) {
          onOtherSettingChange(parsedData.otherSetting);
        }

        showToast('データを読み込みました', 'success');
      } else {
        showToast('保存されたデータがありません', 'info');
      }
    } catch (error) {
      showToast('読み込みに失敗しました', 'error');
    }
  }, [canRestorePokemon, onOtherSettingChange, showToast]);

  // 右側の更新時にも左側の情報を自動保存
  useEffect(() => {
    if (!isInitialized || !selectedPokemon) {
      return;
    }
    saveLeftSideToStorage(false);
  }, [
    attackPokemonList,
    defencePokemonList,
    isInitialized,
    selectedPokemon,
    saveLeftSideToStorage
  ]);

  // Expose save/load functions to parent component
  useEffect(() => {
    if (onSave) {
      (window as any).pokemonSelectSave = handleSaveToStorage;
    }
    if (onLoad) {
      (window as any).pokemonSelectLoad = handleLoadFromStorage;
    }
  }, [onSave, onLoad, selectedPokemon, statInputs, selectedDefenseItem, handleLoadFromStorage, handleSaveToStorage]);

  // Increment/decrement handlers for +/- buttons
  const createIncrementHandler = (statName: EffortField) => {
    return () => {
      const currentEffort = statInputs[statName];
      const nextEffort = currentEffort + 1;
      handleStatChange(statName, nextEffort);
    };
  };

  const createDecrementHandler = (statName: EffortField) => {
    return () => {
      const currentEffort = statInputs[statName];
      const nextEffort = currentEffort - 1;
      handleStatChange(statName, nextEffort);
    };
  };

  // 防御ポケモンの努力値を更新する関数
  const handleUpdateDefensePokemonEffort = useCallback((h_effort: number, b_effort: number, d_effort: number, b_personality?: PersonalityRate, d_personality?: PersonalityRate) => {
    const newStatInputs = {
      ...statInputs,
      hp_effort: h_effort,
      defense_effort: b_effort,
      special_defense_effort: d_effort,
      ...(b_personality !== undefined && { defense_personality: b_personality }),
      ...(d_personality !== undefined && { special_defense_personality: d_personality })
    };
    setStatInputs(newStatInputs);

    if (selectedPokemon) {
      const tempPokemon = { ...selectedPokemon };
      tempPokemon.effective_slider_step.hp = clampEffortPoint(h_effort);
      tempPokemon.effective_slider_step.defense = clampEffortPoint(b_effort);
      tempPokemon.effective_slider_step.special_defense = clampEffortPoint(d_effort);
      if (b_personality !== undefined) {
        tempPokemon.personality.defense = b_personality;
      }
      if (d_personality !== undefined) {
        tempPokemon.personality.special_defense = d_personality;
      }
      setSelectedPokemon(tempPokemon);
      debouncedShowCalculationToast();
    }
  }, [statInputs, selectedPokemon, setStatInputs, debouncedShowCalculationToast]);

  // 現在のモードに応じて表示するポケモンリストと設定関数を選択
  const currentPokemonList = currentMode === 'attack' ? attackPokemonList : defencePokemonList;
  const setCurrentPokemonList = currentMode === 'attack' ? setAttackPokemonList : setDefencePokemonList;

  // 現在のモードに応じてポケモン追加関数を選択
  const currentHandleAddPokemon = currentMode === 'attack' ? handleAddDefenseTargetPokemon : handleAddAttackPokemon;

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* LeftSideBar用のViewModelを作成して渡す（UI専用化の一環） */}
      <LeftSidebar
        vm={useLeftSideBarViewModel({
          selectedPokemon,
          setSelectedPokemon,
          pokemonOptions,
          handlePokemonSelect,
          statInputs,
          selectedDefenseItem,
          setSelectedDefenseItem,
          handleStatChange,
          handlePersonalityChange,
          createIncrementHandler,
          createDecrementHandler,
          totalEvs,
          handleSelectAbilityForDefensePokemon,
          getDefenseItems: getAllItems,
          handleIndividualValueChange,
          selectedMoves: selectedPokemon?.selected_moves || [],
          availableMoves,
          onMoveSelect: handleMoveSelect,
          getMoveDisplayName,
          getMoveDisplayPower,
          currentMode,
          setCurrentMode,
          isComprehensiveMode,
          setIsComprehensiveMode,
        })}
      />

      <MainDisplay
        vm={{
          selectedPokemon,
          selectedPokemonForComprehensive,
          pokemonOptions,
          attackPokemonList: currentPokemonList,
          setAttackPokemonList: setCurrentPokemonList,
          selectedAttackPokemonForMove,
          setSelectedAttackPokemonForMove,
          rightSidebarOpen,
          currentOtherSetting,
          onOtherSettingChange,
          handleAddAttackPokemon: currentHandleAddPokemon,
          getAllAttackMoves,
          handleSelectMoveForAttackPokemon,
          getMoveDisplayName,
          getMoveDisplayPower,
          calculateDamage,
          getDamageDetail,
          getRankTypeForMove,
          handleUpdateRankCorrection,
          handleSelectTerasuTypeForAttackPokemon,
          handleSelectTerasuStateForAttackPokemon,
          handleSelectItemForAttackPokemon,
          getOffenseItems: getAllItems,
          handleSelectAbilityForAttackPokemon,
          handleUpdateKodaiQuarkStat,
          handleUpdateAttackPokemonStats,
          handleUpdateAttackPokemonPersonality,
          handleUpdateAttackPokemonRank,
          onToggleRightSidebar,
          handleRemoveAttackPokemon,
          handleCopyAttackPokemon,
          handleUpdateCriticalHit,
          handleUpdateAttackPokemonStatus,
          handleUpdateCustomPower,
          handleUpdateJuuden,
          handleUpdateDoublePower,
          handleUpdateDefensePokemonEffort,
          currentMode,
          calculateAttackModeDamage,
          getAttackModeDamageDetail,
          isComprehensiveMode,
          setIsComprehensiveMode,
          comprehensiveOpponents,
          selectedOpponentId,
          comprehensiveUiFlags,
          handleAddComprehensiveOpponent,
          handleSelectComprehensiveOpponent,
          handleRemoveComprehensiveOpponent,
          handleCopyComprehensiveOpponent,
          handleUpdateComprehensiveAssumedMove,
          handleUpdateComprehensiveOtherSetting,
          handleUpdateComprehensiveSpeedSettings,
          handleCreateComprehensiveDefenseProfile,
          handleCreateComprehensiveOffenseProfile,
          setComprehensiveUiFlags,
          handleUpdateComprehensiveProfileStats,
          handleUpdateComprehensiveProfilePersonality,
          handleUpdateComprehensiveProfileRank,
          handleUpdateComprehensiveProfileAbility,
          handleUpdateComprehensiveProfileItem,
          handleUpdateComprehensiveProfileTera,
          handleUpdateComprehensiveKodaiQuarkStat,
          handleUpdateComprehensiveProfileStatus,
        }}
      />


      {/* Toast Messages - 複数のトーストを重ねて表示 */}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.durationMs}
          onClose={handleToastClose(toast.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            // 複数のトーストを縦に重ねて表示
            transform: `translateY(-${index * 60}px)`,
          }}
        >
          <Alert
            onClose={handleToastClose(toast.id)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};

export default PokemonSelectPage;
